import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from "react";
import {
  CommentaryItem,
  MarketPrice,
  SocketContextProps,
} from "../utils/interfaces";
import { getAuthHeaders } from "../utils/authHeaders";
import {
  TRADER_MATCH_SNAPSHOT_POLL_MS,
  TRADER_MATCH_SNAPSHOT_SKIP_IF_SSE_RECENT_MS,
} from "../components/Games/traderConstants";
import { consumePrimedMatchInitialState } from "../utils/streamInitialStatePrimedCache";
import { unwrapStreamMarketUpdate } from "../utils/unwrapStreamMarketUpdate";
import {
  commentIndicatesCricketMatchResult,
  cricketPrimaryScoreLineEmpty,
  cricketPrimaryScoresLookEmpty,
  cricketScoreLinesTotalRuns,
  eventStatusToNumber,
  IN_PLAY_STATUS_IDS,
  isCricketRrrComment,
  isDecimalTeamWinner,
  isMatchFinishedStatus,
  isMatchScheduledStatus,
  resolveCricketMatchFinished,
  resultCommentFromEventStatus,
} from "../components/Trader/Cricket/decimalEventStatus";
import { hasExtremeProbabilities, isCricketMatchWinnerForTrade } from "../components/Trader/Cricket/decimalMarketUtils";
import { filterToCountedBallResults } from "../components/Squares/shared/squaresBallResults";
import { parseCricketFixtureFormatOvers } from "../components/Cricket/cricketMatchCardOverDisplay";
import { scoreboardOversReduced } from "../utils/cricketReducedOvers";
import { ballResultsFromEventScore, battingAnalysisFromMessage } from "../utils/cricketLiveBallSignals";
import {
  CRICKET_TEST_FORMAT_CODE,
  isCricketTestMaxOvers,
  normalizeCricketFormatCode,
} from "../utils/cricketTestMatch";
import { mergeStreamCommentaryPayload } from "../utils/mergeStreamCommentary";
import { mergeDecimalScorecardDelta } from "../utils/mergeDecimalScorecardDelta";
import { extractLatestBowlingRowFromScoregrid } from "../utils/scoregridLatestBowling";

type CommentaryBallRow = { ball?: number | string; commentary?: string };

function isNonOutcomeCommentaryResult(resultText: string): boolean {
  const s = String(resultText || "").trim().toLowerCase();
  if (!s) return false;
  return /umpire|review|penalty|\bpen\b|wide|\bwd\b|no\s*ball|\bnb\b|dead\s*ball|lost\s*ball|retired|timed\s*out/.test(s);
}

/** Distinct deliveries at the same display ball (e.g. wide then legal re-bowl) need separate keys
 * when ball ids are coarse. Fine-grained ids uniquely identify one physical delivery — including
 * when Decimal reclassifies a wide/no-ball as a legal outcome on the same id — so those share a key.
 */
function fullCommEntryKey(insKey: string, ball: number | string, commentary: string): string {
  const ballValue = typeof ball === "number" ? ball : parseFloat(String(ball));
  const over0 = Math.floor(ballValue);
  const parts = Number.isFinite(ballValue) ? ballValue.toFixed(1).split(".") : [];
  const delivery1 = parts.length > 1 ? parseInt(parts[1], 10) : 0;
  const coarse =
    !Number.isFinite(ballValue) ||
    !Number.isFinite(delivery1) ||
    delivery1 < 1 ||
    Math.abs(ballValue - (over0 + delivery1 / 10)) < 1e-9;
  if (!coarse) {
    return `${insKey}-${ballValue}`;
  }
  const result = commentary.split(",")[1]?.trim() ?? "";
  const extraTag = isNonOutcomeCommentaryResult(result) ? "E" : "L";
  return `${insKey}-${ballValue}-${extraTag}`;
}

function parseCommentaryBallFields(ball: number | string | undefined): { over0: number; delivery1: number; ballValue: number } {
  const ballValue = typeof ball === "number" ? ball : parseFloat(String(ball ?? ""));
  const over0 = Math.floor(ballValue);
  const parts = ballValue.toFixed(1).split(".");
  const delivery1 = parts.length > 1 ? parseInt(parts[1], 10) : 0;
  return { over0, delivery1, ballValue };
}

function fullCommItemKey(item: CommentaryItem): string {
  const ball = item.feedBall ?? item.over + item.ball / 10;
  return fullCommEntryKey(item.inns, ball, item.commentary ?? "");
}

function fullCommCanonicalBall(item: CommentaryItem): number {
  const ball = item.feedBall ?? item.over + item.ball / 10;
  const { over0, delivery1 } = parseCommentaryBallFields(ball);
  return over0 + delivery1 / 10;
}

/** Matches page display: newest balls first; legal re-bowl above wide/no-ball at same display ball. */
function compareFullCommDisplayOrder(a: CommentaryItem, b: CommentaryItem): number {
  if (a.inns !== b.inns) return a.inns.localeCompare(b.inns);
  const ca = fullCommCanonicalBall(a);
  const cb = fullCommCanonicalBall(b);
  if (ca !== cb) return cb - ca;

  const extraA = isNonOutcomeCommentaryResult(a.result ?? a.commentary.split(",")[1]?.trim() ?? "") || /\bwide\b|\bwd\b|\bno\s*ball\b|\bnb\b/.test(a.commentary.toLowerCase()) ? 1 : 0;
  const extraB = isNonOutcomeCommentaryResult(b.result ?? b.commentary.split(",")[1]?.trim() ?? "") || /\bwide\b|\bwd\b|\bno\s*ball\b|\bnb\b/.test(b.commentary.toLowerCase()) ? 1 : 0;
  if (extraA !== extraB) return extraA - extraB;

  const fineA = typeof a.feedBall === "number" ? a.feedBall : parseFloat(String(a.feedBall ?? fullCommCanonicalBall(a)));
  const fineB = typeof b.feedBall === "number" ? b.feedBall : parseFloat(String(b.feedBall ?? fullCommCanonicalBall(b)));
  return fineB - fineA;
}

/**
 * Merge Delta `commentary.commentaries` into the object TraderCommentary / Squares read (`commentary` state).
 * Merges by ball key so single-ball SSE updates accumulate (see mergeStreamCommentaryPayload).
 */
function mergeCommentariesIntoState(
  prevCommentary: { commentaries?: Record<string, CommentaryBallRow[]> } | null | undefined,
  incomingPayload: { commentaries?: Record<string, CommentaryBallRow[]> }
) {
  const merged = mergeStreamCommentaryPayload(prevCommentary, incomingPayload);
  return merged ?? prevCommentary ?? {};
}

/** `runs/wkts` line for innings totals — avoids `"undefined/undefined"` from `{}` deltas. */
function inningRunsWktsLine(inns: unknown): string {
  if (inns == null || typeof inns !== "object" || Array.isArray(inns)) return "0/0";
  const runsRaw = (inns as { runs?: unknown }).runs;
  const wktsRaw = (inns as { wkts?: unknown }).wkts;
  const r = typeof runsRaw === "number" ? runsRaw : Number(runsRaw);
  const w = typeof wktsRaw === "number" ? wktsRaw : Number(wktsRaw);
  const hasR = runsRaw != null && String(runsRaw).trim() !== "" && Number.isFinite(r);
  const hasW = wktsRaw != null && String(wktsRaw).trim() !== "" && Number.isFinite(w);
  if (!hasR && !hasW) return "0/0";
  return `${hasR ? r : 0}/${hasW ? w : 0}`;
}

/** Sum of `inns1..inns4` runs, or null when no innings carries a usable total. */
function scorecardTotalRuns(card: unknown): number | null {
  if (card == null || typeof card !== "object") return null;
  let total: number | null = null;
  for (const key of ["inns1", "inns2", "inns3", "inns4"] as const) {
    const inns = (card as Record<string, unknown>)[key];
    if (inns == null || typeof inns !== "object") continue;
    const runsRaw = (inns as { runs?: unknown }).runs;
    const runs = typeof runsRaw === "number" ? runsRaw : Number(runsRaw);
    if (runsRaw == null || String(runsRaw).trim() === "" || !Number.isFinite(runs)) continue;
    total = (total ?? 0) + runs;
  }
  return total;
}

/** True when `incoming` carries a lower aggregate total than what is already held — i.e. it is older. */
function scorecardTotalsRegressed(existing: unknown, incoming: unknown): boolean {
  const prev = scorecardTotalRuns(existing);
  const next = scorecardTotalRuns(incoming);
  return prev != null && next != null && next < prev;
}

type WinProbSelectionInput = { name?: string; probability?: number };

/**
 * `subscribeToMatch(…, feedFilter)` lists which message families to apply. When
 * `market_update` is omitted, we still read win-probability from the payload and
 * status fields on `market_update`, but we do not merge the full book into
 * `trading` / `marketData` (BULLION played view has no live order book).
 */
function feedWantsMarkets(feedFilter: unknown): boolean {
  if (feedFilter == null) return true;
  if (!Array.isArray(feedFilter)) return true;
  if (feedFilter.length === 0) return true;
  return (feedFilter as string[]).includes("market_update");
}

type EventScoreboardRow = { name?: string; value?: unknown };

/** Lobby-tier SSE may send `event` without a scoreboard array — never call `.find` on undefined. */
function eventScoreboardRows(event: { scoreboard?: unknown } | null | undefined): EventScoreboardRow[] {
  const sb = event?.scoreboard;
  return Array.isArray(sb) ? (sb as EventScoreboardRow[]) : [];
}

/** Appendix 4 final + common status strings — used so out-of-order last_update does not drop settlement rows. */
function liveMarketRowIndicatesFinalSettlement(m: unknown): boolean {
  if (!m || typeof m !== "object") return false;
  const row = m as Record<string, unknown>;
  const ss = row.settled_status ?? row.Settled_status;
  if (ss === 2 || ss === 3 || ss === 4) return true;
  const su = String(ss ?? "").trim().toUpperCase();
  if (su === "SETTLED" || su === "VOID" || su === "DEADHEAT") return true;
  if (su === "2" || su === "3" || su === "4") return true;
  const st = String(row.status ?? row.market_status ?? row.state ?? "").toLowerCase();
  return (
    st.includes("settled") ||
    st.includes("closed") ||
    st.includes("result") ||
    st.includes("complete")
  );
}

/** Same key as markets.jsx getMarketKey (market_id + market_line_id). */
function getStreamMarketKey(p: { market_id?: unknown; market_line_id?: unknown; line?: unknown }): string {
  const mid = p?.market_id != null ? String(p.market_id) : "";
  const lid =
    p?.market_line_id != null && String(p.market_line_id).trim() !== "" ? String(p.market_line_id) : "";
  if (mid && lid) return `${mid}_${lid}`;
  if (lid) return lid;
  if (mid && p?.line != null && String(p.line).trim() !== "") {
    const n = Number(p.line);
    return `${mid}_line_${Number.isFinite(n) ? String(n) : String(p.line).trim()}`;
  }
  return mid || "unknown";
}

/**
 * Apply SSE/HTTP market rows to the in-memory book.
 * - `fullSnapshot`: authoritative offerable book — upsert incoming keys and delete any key not present
 *   (events server sends this for subscribe/initial-state and for live ticks built from the full cache).
 * - Otherwise merge by key (sparse overlays); Appendix-4 finals delete that key.
 * Settled keys are also removed via `settled_market_keys` on the payload (SSE omits settled rows).
 */
function cricketSelectionHasOfferableOdds(selection: unknown): boolean {
  if (!selection || typeof selection !== "object") return false;
  const n = Number(
    (selection as { recommended_price?: unknown; odds?: unknown }).recommended_price ??
      (selection as { odds?: unknown }).odds
  );
  return Number.isFinite(n) && n > 1.02;
}

function cricketMarketHasOfferableOdds(market: unknown): boolean {
  if (!market || typeof market !== "object") return false;
  const list =
    (market as { selectionslist?: unknown[] }).selectionslist ??
    (market as { selections?: unknown[] }).selections ??
    [];
  return Array.isArray(list) && list.some(cricketSelectionHasOfferableOdds);
}

/** Match Winner: keep last priced runners when a tick omits them or sends a pause blank. */
function retainCricketMatchWinnerOdds(prev: unknown, incoming: unknown): unknown {
  if (!incoming || typeof incoming !== "object") return incoming;
  const isMw = isCricketMatchWinnerForTrade(incoming) || isCricketMatchWinnerForTrade(prev);
  if (!isMw) return incoming;
  if (cricketMarketHasOfferableOdds(incoming)) return incoming;
  if (!prev || typeof prev !== "object" || !cricketMarketHasOfferableOdds(prev)) return incoming;
  const prevSels =
    (prev as { selectionslist?: unknown[] }).selectionslist ??
    (prev as { selections?: unknown[] }).selections;
  return { ...incoming, selectionslist: prevSels, selections: undefined };
}

function applyMarketsFromStreamMessage(
  map: Map<string, unknown>,
  rawMarkets: unknown[],
  options: { fullSnapshot?: boolean; staleSettledOnly?: boolean }
): void {
  if (options.staleSettledOnly) {
    rawMarkets.forEach((market) => {
      if (liveMarketRowIndicatesFinalSettlement(market)) {
        map.delete(getStreamMarketKey(market as { market_id?: unknown; market_line_id?: unknown }));
      }
    });
    return;
  }
  if (options.fullSnapshot) {
    const keepKeys = new Set<string>();
    rawMarkets.forEach((market) => {
      const key = getStreamMarketKey(market as { market_id?: unknown; market_line_id?: unknown });
      if (liveMarketRowIndicatesFinalSettlement(market)) {
        map.delete(key);
        return;
      }
      keepKeys.add(key);
      map.set(key, retainCricketMatchWinnerOdds(map.get(key), market));
    });
    for (const key of [...map.keys()]) {
      if (keepKeys.has(key)) continue;
      if (isCricketMatchWinnerForTrade(map.get(key))) continue;
      map.delete(key);
    }
    return;
  }
  rawMarkets.forEach((market) => {
    const key = getStreamMarketKey(market as { market_id?: unknown; market_line_id?: unknown });
    if (liveMarketRowIndicatesFinalSettlement(market)) {
      map.delete(key);
      return;
    }
    map.set(key, retainCricketMatchWinnerOdds(map.get(key), market));
  });
}

/** Remove markets the server already marked Appendix-4 final (omitted from offerable `markets[]`). */
function applySettledMarketKeyRemovals(map: Map<string, unknown>, parsedData: Record<string, unknown>): boolean {
  const mu = parsedData.market_update as Record<string, unknown> | null | undefined;
  const nestedMu = mu?.market_update as Record<string, unknown> | null | undefined;
  const raw = mu?.settled_market_keys ?? nestedMu?.settled_market_keys ?? parsedData.settled_market_keys;
  if (!Array.isArray(raw) || raw.length === 0) return false;
  let removed = false;
  for (const k of raw) {
    if (k == null) continue;
    const key = String(k);
    if (!key || key === "unknown") continue;
    if (map.delete(key)) removed = true;
  }
  return removed;
}

/** True when this payload’s markets array is the full offerable book (replace missing keys). */
function streamMessageIsMarketSnapshot(parsedData: {
  market_snapshot?: unknown;
  market_update?: { market_snapshot?: unknown } | null;
}): boolean {
  return parsedData.market_snapshot === true || parsedData.market_update?.market_snapshot === true;
}

function normalizeTeamLabel(s: string): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** True when a book selection label refers to the same side as a fixture team (exact or substring for nicknames). */
function teamLabelsMatch(selectionName: string, fixtureTeamName: string): boolean {
  const a = normalizeTeamLabel(selectionName);
  const b = normalizeTeamLabel(fixtureTeamName);
  if (!a || !b) return false;
  if (a === b) return true;
  const minLen = Math.min(a.length, b.length);
  if (minLen < 4) return false;
  return a.includes(b) || b.includes(a);
}

/** Decimal sends match-winner probability as 0–1 fraction; tolerate 0–100. */
function probabilityPercentFromRaw(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n <= 1) return Math.round(n * 100);
  return Math.round(Math.min(100, n));
}

/**
 * Prefer home/away from the same stream payload, else "Team A v Team B" from scorecard description.
 */
function resolveTeamOrderFromStreamPayload(
  parsedData:
    | {
        event?: { scoreboard?: Array<{ name?: string; value?: unknown }> };
        scorecard?: { description?: string };
      }
    | null
    | undefined,
  skipCricketSnapshot: boolean
): string[] | undefined {
  if (skipCricketSnapshot || !parsedData) return undefined;
  const scoreboard = parsedData.event?.scoreboard;
  if (Array.isArray(scoreboard)) {
    const homeObj = scoreboard.find((item) => item.name === "home");
    const awayObj = scoreboard.find((item) => item.name === "away");
    const h = homeObj?.value != null ? String(homeObj.value).trim() : "";
    const a = awayObj?.value != null ? String(awayObj.value).trim() : "";
    if (h && a) return [h, a];
  }
  const desc = parsedData.scorecard?.description;
  if (desc != null && String(desc).trim() !== "") {
    const matchParts = String(desc).split(",");
    const raw = matchParts[0] ?? "";
    const split = raw
      .split(" v ")
      .map((s) => s.trim())
      .filter(Boolean);
    if (split.length >= 2) return [split[0], split[1]];
  }
  return undefined;
}

/**
 * Map Match Winner (1010) selections (arbitrary API order) onto `teamOrder[0]` / `teamOrder[1]` for the UI.
 */
function alignWinProbabilityPairToTeamOrder(
  teamOrder: string[],
  sel: WinProbSelectionInput[]
): { name: string; probability: number }[] | null {
  if (!sel || sel.length < 2) return null;
  const t0 = String(teamOrder[0] ?? "").trim();
  const t1 = String(teamOrder[1] ?? "").trim();
  const s0name = String(sel[0]?.name ?? "").trim();
  const s1name = String(sel[1]?.name ?? "").trim();
  const pFirst = probabilityPercentFromRaw(sel[0]?.probability);
  const pSecond = 100 - pFirst;

  if (!t0 || !t1) {
    return [
      { name: s0name, probability: pFirst },
      { name: s1name, probability: pSecond },
    ];
  }

  const slotFor = (selName: string): 0 | 1 | null => {
    if (!selName) return null;
    const m0 = teamLabelsMatch(selName, t0);
    const m1 = teamLabelsMatch(selName, t1);
    if (m0 && m1) return null;
    if (m0) return 0;
    if (m1) return 1;
    return null;
  };

  const slotS0 = slotFor(s0name);
  const slotS1 = slotFor(s1name);

  let pForT0: number | null = null;
  let pForT1: number | null = null;
  if (slotS0 === 0) pForT0 = pFirst;
  else if (slotS0 === 1) pForT1 = pFirst;
  if (slotS1 === 0) pForT0 = pSecond;
  else if (slotS1 === 1) pForT1 = pSecond;

  if (pForT0 != null && pForT1 == null) {
    pForT1 = 100 - pForT0;
  } else if (pForT1 != null && pForT0 == null) {
    pForT0 = 100 - pForT1;
  } else if (pForT0 != null && pForT1 != null) {
    pForT1 = 100 - pForT0;
  } else if (teamLabelsMatch(s0name, t1) && teamLabelsMatch(s1name, t0)) {
    return [
      { name: t0, probability: pSecond },
      { name: t1, probability: pFirst },
    ];
  } else {
    return [
      { name: s0name || t0, probability: pFirst },
      { name: s1name || t1, probability: pSecond },
    ];
  }

  return [
    { name: t0, probability: pForT0 ?? 0 },
    { name: t1, probability: pForT1 ?? 0 },
  ];
}

export const SocketContext = createContext<SocketContextProps | undefined>(
  undefined
);

/** Latest provider value for `useSocketSelector` (avoids re-rendering all `useSocket()` consumers). */
const socketStoreSnapshotRef: { current: SocketContextProps | null } = { current: null };
const socketStoreListenersRef: { current: Set<() => void> } = { current: new Set() };

function notifySocketStoreListeners() {
  socketStoreListenersRef.current.forEach((listener) => listener());
}

/**
 * Subscribe to a slice of socket state. Re-renders only when the selected value changes
 * (`isEqual`), not when unrelated Trader fields (e.g. `marketData`) update.
 */
export function useSocketSelector<T>(
  selector: (ctx: SocketContextProps) => T,
  isEqual: (prev: T, next: T) => boolean = Object.is
): T {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    throw new Error("useSocketSelector must be used within a SocketProvider");
  }

  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;

  const sliceRef = useRef<T>(selector(ctx));

  return useSyncExternalStore(
    (onStoreChange) => {
      const listener = () => {
        const snap = socketStoreSnapshotRef.current;
        if (!snap) return;
        const next = selectorRef.current(snap);
        if (!isEqualRef.current(sliceRef.current, next)) {
          sliceRef.current = next;
          onStoreChange();
        }
      };
      socketStoreListenersRef.current.add(listener);
      return () => {
        socketStoreListenersRef.current.delete(listener);
      };
    },
    () => sliceRef.current,
    () => sliceRef.current
  );
}

/** Stable stream actions — do not change when cricket/market feed state updates. */
export function useSocketActions(): Pick<
  SocketContextProps,
  "subscribeToMatch" | "getCompletedData" | "subscribeToMatches"
> {
  return useSocketSelector(
    (ctx) => ({
      subscribeToMatch: ctx.subscribeToMatch,
      getCompletedData: ctx.getCompletedData,
      subscribeToMatches: ctx.subscribeToMatches,
    }),
    (a, b) =>
      a.subscribeToMatch === b.subscribeToMatch &&
      a.getCompletedData === b.getCompletedData &&
      a.subscribeToMatches === b.subscribeToMatches
  );
}

export const DecimalSocketProvider = ({
  children,
}: {
  children: ReactNode;
}) => {

  //! State Begin
  const [matchTitle, setMatchTitle] = useState("");
  const [marketData, setMarketData] = useState<any[]>([]);

  // --- home states ---
  const [location, setLocation] = useState();
  const [tournament, setTournament] = useState("");
  const [partnership, setPartnership] = useState({});
  const [matchName, setMatchName] = useState("");
  const [probability, setProbability] = useState([
    { name: "", probability: 0 },
    { name: "", probability: 0 },
  ]);
  const [teams, setTeams] = useState(["", ""]);
  const teamsRef = useRef<string[]>(["", ""]);
  useLayoutEffect(() => {
    teamsRef.current = [teams[0] ?? "", teams[1] ?? ""];
  }, [teams]);

  /** Keep last-known team names when a partial scoreboard/event frame omits home or away. */
  const applyTeamsFromFeed = useCallback((homeVal: unknown, awayVal: unknown) => {
    const prev = teamsRef.current;
    const homeRaw = homeVal != null ? String(homeVal).trim() : "";
    const awayRaw = awayVal != null ? String(awayVal).trim() : "";
    const home = homeRaw || prev[0] || "";
    const away = awayRaw || prev[1] || "";
    if (home === (prev[0] ?? "") && away === (prev[1] ?? "")) return;
    setTeams([home, away]);
  }, []);

  const applyTeamsFromMatchTitlePart = useCallback((titlePart: string) => {
    const chunks = String(titlePart ?? "")
      .split(" v ")
      .map((s) => s.trim())
      .filter(Boolean);
    if (chunks.length < 2) return;
    applyTeamsFromFeed(chunks[0], chunks[1]);
  }, [applyTeamsFromFeed]);
  const [game_phase, setGamePhase] = useState("");
  const [prematch, setPrematch] = useState(true);
  const prematchRef = useRef(true);
  useEffect(() => {
    prematchRef.current = prematch;
  }, [prematch]);
  const [localTime, setLocalTime] = useState("");
  const [batting, setBatting] = useState(null);
  const [status, setStatus] = useState("");
  const [curBall, setCurBall] = useState<string | null>(null);
  const [curInnsOver, setCurInnsOver] = useState("");
  const [overDelivery, setOverDelivery] = useState<string[]>([]);
  const [commentary, setCommentary] = useState<any>({});
  const [battings, setBattings] = useState<string[]>([]);
  const [homeOvers, setHomeOvers] = useState<string[]>([]);
  const [awayOvers, setAwayOvers] = useState<string[]>([]);
  const [mOvers, setMOvers] = useState<string[]>([]);
  const [innsNow, setInnsNow] = useState<number>(0);
  const [batNow, setBatNow] = useState<string>("");
  const [liveBat, setLiveBat] = useState<string>("");
  const [CurPos, setCurPos] = useState([0, 0, 0]);
  const [score, setScore] = useState(["0/0", "0/0"]);
  const [overs, setOvers] = useState([""]);

  // --- score card states ---
  const [scoregrid, setScoregrid] = useState<(string | number)[]>([]);
  const [matchComment, setMatchComment] = useState("");
  const [runRate, setRunRate] = useState("");
  const [currentInnings, setCurrentInnings] = useState(0);

  // --- match details ---
  const [format, setFormat] = useState<number | null>(null);
  /** Live scoreboard "Max Overs" (current innings cap; DLS / reduced overs). */
  const [scoreboardMaxOvers, setScoreboardMaxOvers] = useState<number | null>(null);
  /** Decimal reduced1/reduced2/reduced_match below 1 — rain-reduced; feed has no revised target. */
  const [oversReduced, setOversReduced] = useState(false);
  /** Competition gender from Delta lineups / event (e.g. M, F) — used for Squares run-distribution model. */
  const [competitionGender, setCompetitionGender] = useState<string | undefined>(undefined);
  const [trading, setTrading] = useState<MarketPrice[]>([]);
  const [fullComm, setFullComm] = useState<CommentaryItem[]>([]);
  const [striker, setStriker] = useState("");
  const [battingOvers, setBattingOvers] = useState([0, 0, 0, 0]);
  const [incomingEvent, setIncomingEvent] = useState(false);
  const [stream, setStream] = useState(true);
  const [subHeader, setSubHeader] = useState<string>("standard");
  const [embedcode, setEmbedCode] = useState("");
  const [lineup, setLineup] = useState<any[]>([]);
  const [scoreGridData, setScoreGridData] = useState<string[]>([]);
  /**
   * When a scoregrid SSE message is dropped as stale (last_update) but carries more counted balls for the
   * same over as the last applied scoregrid, keep that delivery for Squares only (see SquaresMatchPlayContent).
   */
  const [squaresStaleScoregridBallSupplement, setSquaresStaleScoregridBallSupplement] = useState<{
    matchId: string;
    inns: number;
    ov: number;
    delivery: unknown[];
  } | null>(null);
  const [scorecardData, setScorecardData] = useState<any[]>([]);
  /** Decimal alerts (flags: powerplay, ball in progress, injury, weather, etc.) for scoreboard modal */
  const [alerts, setAlerts] = useState<any>(null);
  /** Latest Decimal batting_analysis (shot, shot_dir, aerial) for the delivery just played. */
  const [battingAnalysis, setBattingAnalysis] = useState<any>(null);
  /** Latest delta ball results (`event.score`), newest first — carries e.g. "WICKET, Bowled". */
  const [ballResults, setBallResults] = useState<any>(null);
  const [bowling, setBowling] = useState<any>();
  const [feed, setFeed] = useState<any[]>([]);
  const [eventStatus, setEventStatus] = useState<any>();
  const eventStatusRef = useRef<any>(undefined);
  /** Once a fixture is known finished, ignore stale socket frames that reset scores or chase comments. */
  const matchFinishedLatchRef = useRef(false);
  const scoreRef = useRef<string[]>(["0/0", "0/0"]);
  const matchCommentRef = useRef("");
  /** Match-level market open status (`betting_status`: ACTIVE/SUSPENDED) from match_update or market_update. Takes precedence over market-level status (Decimal API). */
  const [matchTradingStatus, setMatchTradingStatus] = useState<string | undefined>();
  const [matchStartDateTime, setMatchStartDateTime] = useState<string | null>(null);
  /** Match ID currently subscribed via subscribeToMatch; used so Markets only merge data for this match. */
  const [subscribedMatchId, setSubscribedMatchId] = useState<string | null>(null);
  /** Decimal feed state: "recovering" (catch-up) or "realtime". When recovering we replace accumulated markets instead of merge. */
  const [subscriptionState, setSubscriptionState] = useState<string | undefined>();
  /** True when the match EventSource has errored/closed (lost connection); cleared when reconnecting or on cleanup. */
  const [connectionLost, setConnectionLost] = useState(false);
  /** Events server bumps this when Squares leaderboards / ball_results are written so match play refetches `/squares/match-over-plays`. */
  const [squaresOversRefreshNonce, setSquaresOversRefreshNonce] = useState(0);
  /** Bumped when BULLION live settlement writes plays for this match (`bullion_settlement_updated` SSE). */
  const [bullionSettlementRefresh, setBullionSettlementRefresh] = useState<{
    nonce: number;
    matchId: string;
    gameIds: number[];
  }>({ nonce: 0, matchId: "", gameIds: [] });

  // --- trader-match-card

  //! State End

  useLayoutEffect(() => {
    eventStatusRef.current = eventStatus;
    if (isMatchFinishedStatus(eventStatus)) {
      matchFinishedLatchRef.current = true;
    }
  }, [eventStatus]);

  useLayoutEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useLayoutEffect(() => {
    matchCommentRef.current = matchComment;
    if (commentIndicatesCricketMatchResult(matchComment)) {
      matchFinishedLatchRef.current = true;
    }
  }, [matchComment]);

  const applyEventStatusFromFeed = useCallback((next: unknown) => {
    if (next == null) return;
    const prev = eventStatusRef.current;
    if (isMatchFinishedStatus(prev) && !isMatchFinishedStatus(next)) return;
    if (matchFinishedLatchRef.current && isMatchScheduledStatus(next) && !isMatchFinishedStatus(next)) {
      return;
    }
    // Catalog / thin frames often replay MATCH_SCHEDULED (0) after the match is already live or on a break.
    const prevN = eventStatusToNumber(prev);
    if (
      isMatchScheduledStatus(next) &&
      !isMatchFinishedStatus(next) &&
      prevN != null &&
      (IN_PLAY_STATUS_IDS as readonly number[]).includes(prevN)
    ) {
      return;
    }
    if (isMatchFinishedStatus(next)) {
      matchFinishedLatchRef.current = true;
    }
    eventStatusRef.current = next;
    setEventStatus(next);
    setPrematch(isMatchScheduledStatus(next));

    // Go-live while the match page is already open: pull full detail snapshot once.
    const nextN = eventStatusToNumber(next);
    const nextInPlay =
      nextN != null && (IN_PLAY_STATUS_IDS as readonly number[]).includes(nextN);
    if (nextInPlay && isMatchScheduledStatus(prev)) {
      requestLiveDetailHydrateRef.current?.();
    }
  }, []);

  const shouldApplyCricketScoreboardScores = useCallback((nextScores: string[]) => {
    // Live Delta often sends a thin scorecard (inns null / no runs) after a good scoreboard
    // frame. Never wipe known totals with empty placeholders — finished or in-play.
    if (cricketPrimaryScoresLookEmpty(nextScores)) {
      return cricketPrimaryScoresLookEmpty(scoreRef.current);
    }
    // Incomplete scoreboard (e.g. runs.1 missing → "0/0") must not clear one team's
    // already-known innings total after hydrate from scorecard.
    const prev = scoreRef.current;
    if (!Array.isArray(prev) || prev.length < 2) return true;
    for (let i = 0; i < 2; i++) {
      if (cricketPrimaryScoreLineEmpty(nextScores[i]) && !cricketPrimaryScoreLineEmpty(prev[i])) {
        return false;
      }
    }
    // A finished match never scores fewer runs. `/completed/:id` can hold a snapshot archived at
    // MATCH_COMPLETE, before the feed finished correcting the closing deliveries — applying it over
    // fresher stream scores rolls the scoreboard backwards. Compared as a total across all innings
    // slots, since scoreboard and scorecard order those slots by whoever is batting.
    if (!matchFinishedLatchRef.current) return true;
    const nextTotal = cricketScoreLinesTotalRuns(nextScores);
    const prevTotal = cricketScoreLinesTotalRuns(prev);
    if (nextTotal != null && prevTotal != null && nextTotal < prevTotal) return false;
    return true;
  }, []);

  const guardedSetScore = useCallback((nextScores: string[]) => {
    if (!shouldApplyCricketScoreboardScores(nextScores)) return;
    scoreRef.current = nextScores;
    setScore(nextScores);
  }, [shouldApplyCricketScoreboardScores]);

  const shouldApplyCricketMatchComment = useCallback((nextComment: string) => {
    const next = String(nextComment ?? "").trim();
    if (!matchFinishedLatchRef.current) return true;
    if (isCricketRrrComment(next)) return false;
    if (next) return true;
    return !String(matchCommentRef.current ?? "").trim();
  }, []);

  const guardedSetMatchComment = useCallback((nextComment: string) => {
    if (!shouldApplyCricketMatchComment(nextComment)) return;
    matchCommentRef.current = nextComment;
    setMatchComment(nextComment);
  }, [shouldApplyCricketMatchComment]);

  /** Mirrors latest applied scoregrid object for stale-path comparison (not the string[] initial state). */
  const scoreGridDataRef = useRef<unknown>(null);

  // Throttled store for market_update (markets include selections with prices)
  const marketDataMapRef = useRef<Map<string, any>>(new Map());
  const marketDataFlushTimeoutRef = useRef<number | null>(null);
  // Same key as markets.jsx getMarketKey so accumulation and updates line up (market_id + market_line_id per Delta doc).
  const getMarketKey = (p: any) => {
    const mid = p?.market_id != null ? String(p.market_id) : "";
    const lid = p?.market_line_id != null ? String(p.market_line_id) : "";
    if (mid && lid) return `${mid}_${lid}`;
    if (lid) return lid;
    return mid || "unknown";
  };
  const scheduleMarketDataFlush = useCallback(() => {
    if (marketDataFlushTimeoutRef.current !== null) return;
    marketDataFlushTimeoutRef.current = window.setTimeout(() => {
      marketDataFlushTimeoutRef.current = null;
      const arr = Array.from(marketDataMapRef.current.values());
      arr.sort((a, b) => (a.display_order ?? a.market_do ?? 0) - (b.display_order ?? b.market_do ?? 0));
      setMarketData((prev) => {
        if (prev.length !== arr.length) return arr;
        const prevMap = new Map(prev.map((p) => [getMarketKey(p), p]));
        const hasChanged = arr.some((na) => {
          const prevItem = prevMap.get(getMarketKey(na));
          if (!prevItem) return true;
          const sel = (x: any) => x?.selectionslist ?? x?.selections;
          return (
            prevItem.name !== na.name ||
            prevItem.market_type !== na.market_type ||
            (prevItem.display_order ?? prevItem.market_do) !== (na.display_order ?? na.market_do) ||
            prevItem.line !== na.line ||
            prevItem.active !== na.active ||
            prevItem.status !== na.status ||
            JSON.stringify(sel(prevItem)) !== JSON.stringify(sel(na))
          );
        });
        return hasChanged ? arr : prev;
      });
    }, 0);
  }, []);

  // Track current matchId to detect when switching matches
  const currentMatchIdRef = useRef<string | null>(null);
  /**
   * Monotonic `last_update` for live scoreboard + scorecard + top-level scoreboard only.
   * Kept separate from market updates so a newer market tick does not reject a slightly older scorecard frame
   * (or vice versa), which caused flicker and “previous over” UI after reconnect / split SSE messages.
   */
  const latestCricketFeedVersionPerMatchRef = useRef<Map<string, number>>(new Map());
  /** Monotonic version for full market list replacement (`market_update` with non-empty markets). */
  const latestMarketFeedVersionPerMatchRef = useRef<Map<string, number>>(new Map());
  /** Same idea as cricket/market refs, but only for `scoregrid` payloads. Without this, a market-only
   * message can bump a global version and cause a slightly older scoregrid frame to be dropped entirely. */
  const latestScoregridLastUpdatePerMatchRef = useRef<Map<string, number>>(new Map());
  const devLastCountedBallsRef = useRef<{ key: string; counted: number } | null>(null);
  /** Scorecard has its own Decimal last_update clock — sharing the event version lane drops cache-replay
   * scorecards on lobby↔match-play remount (newer event, slightly older scorecard). */
  const latestScorecardLastUpdatePerMatchRef = useRef<Map<string, number>>(new Map());
  /** Per-fixture merged scorecard for Delta partials (`inns` null wipes). SSE match id keyed. */
  const mergedScorecardByMatchIdRef = useRef<Map<string, unknown>>(new Map());
  /** Reuse one active single-match SSE connection across screens (Trader/Squares/Bullion). */
  const sharedSingleMatchSubscriptionRef = useRef<{
    matchId: string;
    subscribers: number;
    close: () => void;
  } | null>(null);
  /**
   * When a match leaves SCHEDULED / pre-match while the page is already open, force one
   * `initial-state` hydrate (scorecard / commentary / lineups / Match Winner). Live event/market
   * SSE otherwise keeps the timed snapshot poll suppressed, so detail tabs stay empty until refresh.
   */
  const requestLiveDetailHydrateRef = useRef<null | (() => void)>(null);
  /** One forced hydrate per match id per visit (go-live only). */
  const liveDetailHydrateDoneRef = useRef<Set<string>>(new Set());

  const parseLastUpdateVersion = useCallback((lastUpdate: unknown): number | null => {
    if (typeof lastUpdate !== "string" || !lastUpdate.trim()) return null;
    const firstPart = lastUpdate.split("-")[0];
    const num = Number(firstPart);
    return Number.isFinite(num) ? num : null;
  }, []);

  const isStaleCricketFeedVersion = useCallback((matchId: string, lastUpdate: unknown): boolean => {
    const incomingVersion = parseLastUpdateVersion(lastUpdate);
    if (incomingVersion == null) return false;
    const prev = latestCricketFeedVersionPerMatchRef.current.get(matchId);
    return prev != null && incomingVersion < prev;
  }, [parseLastUpdateVersion]);

  const bumpCricketFeedVersion = useCallback((matchId: string, lastUpdate: unknown) => {
    const incomingVersion = parseLastUpdateVersion(lastUpdate);
    if (incomingVersion == null) return;
    latestCricketFeedVersionPerMatchRef.current.set(matchId, incomingVersion);
  }, [parseLastUpdateVersion]);

  const isStaleMarketFeedVersion = useCallback((matchId: string, lastUpdate: unknown): boolean => {
    const incomingVersion = parseLastUpdateVersion(lastUpdate);
    if (incomingVersion == null) return false;
    const prev = latestMarketFeedVersionPerMatchRef.current.get(matchId);
    return prev != null && incomingVersion < prev;
  }, [parseLastUpdateVersion]);

  const bumpMarketFeedVersion = useCallback((matchId: string, lastUpdate: unknown) => {
    const incomingVersion = parseLastUpdateVersion(lastUpdate);
    if (incomingVersion == null) return;
    latestMarketFeedVersionPerMatchRef.current.set(matchId, incomingVersion);
  }, [parseLastUpdateVersion]);

  const isStaleScorecardFeedVersion = useCallback((matchId: string, lastUpdate: unknown): boolean => {
    const incomingVersion = parseLastUpdateVersion(lastUpdate);
    if (incomingVersion == null) return false;
    const prev = latestScorecardLastUpdatePerMatchRef.current.get(matchId);
    return prev != null && incomingVersion < prev;
  }, [parseLastUpdateVersion]);

  const bumpScorecardFeedVersion = useCallback((matchId: string, lastUpdate: unknown) => {
    const incomingVersion = parseLastUpdateVersion(lastUpdate);
    if (incomingVersion == null) return;
    latestScorecardLastUpdatePerMatchRef.current.set(matchId, incomingVersion);
  }, [parseLastUpdateVersion]);

  const clearMatchFeedVersionGuards = useCallback((matchId: string) => {
    latestCricketFeedVersionPerMatchRef.current.delete(matchId);
    latestMarketFeedVersionPerMatchRef.current.delete(matchId);
    latestScoregridLastUpdatePerMatchRef.current.delete(matchId);
    latestScorecardLastUpdatePerMatchRef.current.delete(matchId);
  }, []);

  useEffect(() => {
    scoreGridDataRef.current = scoreGridData as unknown;
  }, [scoreGridData]);

  // Cleanup pending flush timers on unmount
  useEffect(() => {
    return () => {
      if (marketDataFlushTimeoutRef.current !== null) {
        clearTimeout(marketDataFlushTimeoutRef.current);
        marketDataFlushTimeoutRef.current = null;
      }
    };
  }, []);

  const getEmbedCode = useCallback(async (match_id) => {
    try {
      const response = await fetch(
        `${process.env.API_URL}/embeddedplayer/${match_id}`,
        { method: "GET", headers: getAuthHeaders() }
      );
      if (response.status === 200) {
        const data = await response.json();
        setEmbedCode(data.data);
      }
    } catch (error) {
      return null;
    }
  }, []);

  const getCompletedData = useCallback(async (match_id) => {
    try {
      const response = await fetch(
        `${process.env.API_URL}/completed/${match_id}`,
        { method: "GET", headers: getAuthHeaders() }
      );

      if (response.status === 200) {
        const data = await response.json();
        const md = data?.data;
        if (md == null || Array.isArray(md)) {
          return null;
        }

        let archiveIsStale = false;

        try {
          const sc0 = md.scorecard?.[0];
          const scMsg = sc0?.msg_data ?? sc0;
          if (scMsg && typeof scMsg === "object") {
            const midStr = String(match_id);
            // `/completed/:id` can hold a snapshot archived at MATCH_COMPLETE, before the feed
            // finished correcting the closing deliveries. Keep its status/teams, drop its scores.
            archiveIsStale = scorecardTotalsRegressed(
              mergedScorecardByMatchIdRef.current.get(midStr) ?? null,
              scMsg
            );
            if (!archiveIsStale) {
              mergedScorecardByMatchIdRef.current.set(midStr, scMsg);
              setScorecardData(scMsg);
            }
            if (scMsg.event_status != null) applyEventStatusFromFeed(scMsg.event_status);
            if (resolveCricketMatchFinished({ eventStatus: scMsg.event_status })) {
              matchFinishedLatchRef.current = true;
            }

            if (typeof scMsg.description === "string" && scMsg.description.includes(",")) {
              const matchParts = scMsg.description.split(",");
              if (matchParts[0]?.includes(" v ")) {
                setMatchTitle(matchParts.length >= 3 ? matchParts[1].trim() : "");
                applyTeamsFromMatchTitlePart(matchParts[0]);
              }
            }

            setInnsNow(scMsg.inns_now);
            setBatNow(scMsg.bat_now);
            const mp = typeof scMsg.description === "string" ? scMsg.description.split(",") : [];
            if (mp[0]?.includes(" v ")) {
              const s1 = inningRunsWktsLine(scMsg.inns1);
              const s2 = inningRunsWktsLine(scMsg.inns2);
              const s3 = inningRunsWktsLine(scMsg.inns3);
              const s4 = inningRunsWktsLine(scMsg.inns4);
              const insScore = [s1, s2, s3, s4];
              const innsnow = scMsg.inns_now;
              let oppnow = 1;
              let rptNow = 2;
              if (innsnow === 1 || innsnow === 3) {
                oppnow = 2;
                rptNow = 1;
              }
              setLiveBat(scMsg.bat_now);
              if (scMsg.bat_now === mp[0].split(" v ")[0]) {
                guardedSetScore([
                  insScore[rptNow - 1],
                  insScore[oppnow - 1],
                  insScore[rptNow + 1],
                  insScore[oppnow + 1],
                ]);
              } else {
                guardedSetScore([
                  insScore[oppnow - 1],
                  insScore[rptNow - 1],
                  insScore[oppnow + 1],
                  insScore[rptNow + 1],
                ]);
              }
            }
          }

          const ev = md.event?.[0]?.msg_data;
          if (ev && Array.isArray(ev.scoreboard) && ev.scoreboard.length > 0) {
            await EventProc(ev, match_id);
          }
        } catch (e) {
          if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
            console.warn("[getCompletedData] hydrate skipped:", e);
          }
        }

        const sg0 = md.scoregrid?.[0];
        const sgMsg = sg0?.msg_data ?? sg0;
        if (!archiveIsStale && sgMsg && typeof sgMsg === "object" && sgMsg.overs) {
          setScoreGridData(sgMsg);
          fetchScoreGrid(match_id, sgMsg);
          setSquaresStaleScoregridBallSupplement((prev) =>
            prev != null && String(prev.matchId) === String(match_id) ? null : prev
          );
        }

        const commRaw = md.commentary;
        let commPayload: { commentaries?: Record<string, CommentaryBallRow[]> } | null = null;
        if (Array.isArray(commRaw) && commRaw.length > 0) {
          const first = commRaw[0];
          commPayload =
            first?.msg_data?.commentaries != null
              ? first.msg_data
              : first?.commentaries != null
                ? first
                : null;
        } else if (commRaw && typeof commRaw === "object" && (commRaw as { commentaries?: unknown }).commentaries) {
          commPayload = commRaw as { commentaries?: Record<string, CommentaryBallRow[]> };
        }
        if (commPayload?.commentaries) {
          setCommentary((prev) => mergeCommentariesIntoState(prev, commPayload));
        }

        const luRaw = md.lineups;
        let lineupsPayload: unknown = null;
        if (Array.isArray(luRaw) && luRaw.length > 0) {
          lineupsPayload = luRaw[0]?.msg_data?.lineups ?? luRaw[0]?.lineups ?? luRaw[0];
        } else if (luRaw && typeof luRaw === "object") {
          const o = luRaw as { msg_data?: { lineups?: unknown }; lineups?: unknown; teams?: unknown };
          lineupsPayload = o.msg_data?.lineups ?? o.lineups ?? (Array.isArray(o.teams) ? o : null);
        }
        if (lineupsPayload && typeof lineupsPayload === "object") {
          setLineup(lineupsPayload as never);
        }
      } else {
        throw new Error("Failed to fetch data");
      }
    } catch (error) {
      //console.error('Error fetching data:', error);
      return null;
    }
    /* EventProc / fetchScoreGrid are stable callbacks defined later in this provider; empty deps avoid TDZ. */
  }, []);

  const applyWinProbabilityUpdate = useCallback((parsedData: any, skipCricketSnapshot: boolean) => {
    const markets = parsedData?.market_update?.markets ?? [];
    const winPredict = markets.find((m: any) => {
      const mid = String(m.market_id ?? "");
      const tid = String(m.market_type_id ?? "");
      return mid === "1010" || tid === "1010" || tid === "10";
    });
    if (!winPredict) return;
    // Decimal sends p >= 0.99 when modelling is uncertain (treat as SUS for trading) — do not paint 99% Win Prob.
    if (hasExtremeProbabilities(winPredict)) return;
    const sel = winPredict.selectionslist ?? winPredict.selections;
    if (!sel || sel.length < 2) return;
    const teamOrderFromPayload = resolveTeamOrderFromStreamPayload(parsedData, skipCricketSnapshot);
    const teamOrder = teamOrderFromPayload ?? teamsRef.current;
    const aligned = alignWinProbabilityPairToTeamOrder(teamOrder, sel);
    if (aligned) setProbability(aligned);
  }, []);

  const matchDetails = useCallback((
    data,
    match_id,
    cb,
    opts?: {
      skipCricketSnapshot?: boolean;
      skipMarketData?: boolean;
      skipScorecard?: boolean;
      wantMarkets?: boolean;
    }
  ) => {
    const skipCricketSnapshot = Boolean(opts?.skipCricketSnapshot);
    const skipMarketData = Boolean(opts?.skipMarketData);
    const skipScorecard = Boolean(opts?.skipScorecard);
    const includeMarketBook = opts?.wantMarkets !== false;
    if (data.msg_type && data.last_update) {
      let timeStamp = parseFloat(data.last_update.split("-")[0]);
      const last_update = new Date(timeStamp);
    }
    if (data.lineups) {
      setLineup(data.lineups);
      const g = (data.lineups as any)?.competition?.gender;
      if (g != null && String(g).trim() !== "") {
        setCompetitionGender(String(g).trim());
      }
    }
    if (data.match_update) {
      const normalizedFormat = normalizeCricketFormatCode(data.match_update.format);
      if (normalizedFormat != null) setFormat(normalizedFormat);
      if (data.match_update.betting_status) {
        setMatchTradingStatus(data.match_update.betting_status);
      }
      // Extract match start datetime if available
      if (data.match_update.start_datetime) {
        setMatchStartDateTime(data.match_update.start_datetime);
      }
    }
    // Backend delta stream sends format on event, not match_update
    if (!skipCricketSnapshot && data.event) {
      const fromFormat =
        data.event.format != null ? normalizeCricketFormatCode(data.event.format) : null;
      const fromMaxOvers = isCricketTestMaxOvers(data.event.max_overs)
        ? CRICKET_TEST_FORMAT_CODE
        : data.event.max_overs != null
          ? normalizeCricketFormatCode(data.event.max_overs)
          : null;
      const formatVal = fromFormat ?? fromMaxOvers;
      if (formatVal != null) setFormat(formatVal);
    }
    if (!skipCricketSnapshot && data.event?.gender != null && String(data.event.gender).trim() !== "") {
      setCompetitionGender(String(data.event.gender).trim());
    }
    if (data.market_update && !skipMarketData) {
      if (data.market_update.start_datetime) {
        setMatchStartDateTime(data.market_update.start_datetime);
      }
      if (includeMarketBook) {
        // Backend sends markets already filtered by allowed list; use as-is
        const filteredMarkets = data.market_update.markets ?? [];
        let tm = trading;
        filteredMarkets.forEach((mk, id) => {
          let existingIndex = tm.findIndex(
            (item) => String(item.market_id) === String(mk.market_id)
          );
          if (existingIndex === -1) {
            tm.push(mk);
          } else {
            tm[existingIndex] = mk;
          }
        });
        tm.sort(
          (a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10)
        );
        setTrading(tm);
      }
      applyWinProbabilityUpdate(data, skipCricketSnapshot);
    } //**commentary */

    if (data.commentary) {
      let commentary = data.commentary;
      setCommentary((prevC) => mergeCommentariesIntoState(prevC, commentary));
      // Use functional update to always work with the latest state, preventing duplicates from rapid updates
      setFullComm((prevComms) => {
        // Create a copy of the existing commentary array
        let comms = [...prevComms];
        // Use a Map for O(1) lookup and to track the latest state
        const commsMap = new Map(
          comms.map((item) => [fullCommItemKey(item), item])
        );
        // Track entries processed in this update cycle to prevent duplicates within the same payload
        const processedInThisUpdate = new Set<string>();
        
        for (let inns = 1; inns <= 4; inns++) {
          let insKey = "inns" + inns;
          if (commentary.commentaries[insKey]) {
            commentary.commentaries[insKey].forEach((com, idx) => {
              const { over0, delivery1, ballValue } = parseCommentaryBallFields(com.ball);
              let ov = over0;
              let ba = delivery1;
              let commKey = fullCommEntryKey(insKey, com.ball ?? ballValue, com.commentary ?? "");
              
              // Skip if we've already processed this entry in the current update
              if (processedInThisUpdate.has(commKey)) {
                return; // Skip duplicate within the same payload
              }

              // Fine-id legal outcome replaces any prior row for that exact ball id (e.g. mistaken
              // "wide + 4" corrected to "Four", including legacy keys that tagged extras separately).
              const resultPart = com.commentary?.split(",")[1]?.trim() ?? "";
              const incomingIsExtra =
                isNonOutcomeCommentaryResult(resultPart) ||
                /\bwide\b|\bno\s*ball\b|\bnb\b|\bwd\b/.test(String(com.commentary ?? "").toLowerCase());
              const feedBallValue = typeof com.ball === "number" ? com.ball : ballValue;
              const incomingIsFine =
                Number.isFinite(feedBallValue) &&
                Math.abs(feedBallValue - (over0 + delivery1 / 10)) >= 1e-9;
              if (!incomingIsExtra && incomingIsFine) {
                for (const [k, item] of [...commsMap.entries()]) {
                  if (k === commKey) continue;
                  const itemBall = item.feedBall ?? item.over + item.ball / 10;
                  if (itemBall !== feedBallValue) continue;
                  commsMap.delete(k);
                  const removeIdx = comms.findIndex((c) => fullCommItemKey(c) === k);
                  if (removeIdx !== -1) comms.splice(removeIdx, 1);
                }
              }
              
              // Check if this commentary entry already exists in previous state
              if (commsMap.has(commKey)) {
                // Update existing entry
                const existingItem = commsMap.get(commKey);
                if (existingItem) {
                  const csplit = com.commentary.split(",");
                  const res = csplit[1];
                  const fr = csplit[0].split(" to ")[0];
                  const tr = csplit[0].split(" to ")[1];
                  const updated: CommentaryItem = {
                    ...existingItem,
                    commentary: com.commentary,
                    ballFrom: fr,
                    ballTo: tr,
                    result: res?.trim(),
                    feedBall: com.ball,
                    _corrected: Boolean(com._corrected),
                    _correctedFrom:
                      typeof com._correctedFrom === "string" ? com._correctedFrom : undefined,
                  };
                  commsMap.set(commKey, updated);
                  const idx = comms.findIndex((c) => fullCommItemKey(c) === commKey);
                  if (idx !== -1) comms[idx] = updated;
                }
                processedInThisUpdate.add(commKey);
                return; // Skip adding duplicate
              }
              
              // Add new entry
              let csplit = com.commentary.split(",");
              let res = csplit[1];
              let fr = csplit[0].split(" to ")[0];
              let tr = csplit[0].split(" to ")[1];
              let newObject: CommentaryItem = {
                inns: insKey,
                over: ov,
                ball: ba,
                feedBall: com.ball,
                commentary: com.commentary,
                ballFrom: fr,
                ballTo: tr,
                result: res?.trim(),
                _corrected: Boolean(com._corrected),
                _correctedFrom:
                  typeof com._correctedFrom === "string" ? com._correctedFrom : undefined,
              };
              comms.unshift(newObject);
              commsMap.set(commKey, newObject); // Track this entry to prevent duplicates
              processedInThisUpdate.add(commKey); // Mark as processed in this update
            });
          }
        }
        comms.sort(compareFullCommDisplayOrder);
        return comms;
      });
    }

    // Scorecard uses its own last_update lane — do not gate on the cricket/event version skip.
    if (data.scorecard && !skipScorecard) {
      const mid = match_id != null ? String(match_id) : "";
      const merged = mergeDecimalScorecardDelta(mid ? mergedScorecardByMatchIdRef.current.get(mid) ?? null : null, data.scorecard);
      if (mid) mergedScorecardByMatchIdRef.current.set(mid, merged);
      const scorecard = merged as typeof data.scorecard;
      setScorecardData(scorecard);
      if (scorecard?.event_status != null) {
        applyEventStatusFromFeed(scorecard.event_status);
      }
    }
    if (data.alerts) {
      if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
        console.log("[Alerts] received (initial/cache):", JSON.stringify(data.alerts, null, 2));
      }
      setAlerts(data.alerts);
    }
    const battingAnalysisMsg = battingAnalysisFromMessage(data);
    if (battingAnalysisMsg) setBattingAnalysis(battingAnalysisMsg);
    if (data.event && !skipCricketSnapshot) {
      EventProc(data.event, match_id);
    } else {
      //console.log(`Received: ${match_id} - ${data.data}`);
    }
  }, [trading, applyWinProbabilityUpdate]);

  const EventProc = useCallback(async (event, matchId?: string) => {
    if (!event || typeof event !== "object") return;
    if (event?.gender != null && String(event.gender).trim() !== "") {
      setCompetitionGender(String(event.gender).trim());
    }
    const scoreboard = event.scoreboard;
    if (!Array.isArray(scoreboard) || scoreboard.length === 0) {
      return;
    }
    const getScoreboardValue = (name: string) =>
      scoreboard.find((item) => item.name === name)?.value?.toString?.().trim?.() ?? "";
    const parseScoreInt = (name: string) => {
      const v = parseInt(getScoreboardValue(name), 10);
      return Number.isNaN(v) ? null : v;
    };
    const deriveWinTextFromScoreboard = () => {
      const winnerName = getScoreboardValue("winner");
      const winnerOk = isDecimalTeamWinner(winnerName);
      const possibleMarginKeys = [
        "wonby",
        "won_by",
        "win_by",
        "winning_margin",
        "result",
        "match_result",
        "victory_margin",
      ];
      for (const key of possibleMarginKeys) {
        const val = getScoreboardValue(key);
        if (!val) continue;
        if (/\bwon\b|\bwins?\b|\bruns?\b|\bwickets?\b|\btied?\b|\bdraw/i.test(val)) {
          return val;
        }
        if (winnerOk && /\bruns?\b|\bwickets?\b/i.test(val)) {
          return `${winnerName} won by ${val}`;
        }
      }

      // Decimal completed deltas sometimes leave batting.1 / home empty — use winner as name fallback.
      const bat1 = getScoreboardValue("batting.1") || getScoreboardValue("home");
      const bat2 = getScoreboardValue("batting.2") || getScoreboardValue("away");
      const r1 = parseScoreInt("runs.1");
      const r2 = parseScoreInt("runs.2");
      const w2 = parseScoreInt("wkts.2");
      if (r1 == null || r2 == null) return "";

      // Between innings the chase side is still 0/0 — never invent "Team won by N runs".
      const chaseNotStarted = r2 === 0 && (w2 == null || w2 === 0);

      if (r2 > r1) {
        const name = bat2 || (winnerOk ? winnerName : "");
        if (!name) return "";
        const wicketsInHand = w2 == null ? 10 : Math.max(0, 10 - w2);
        return `${name} won by ${wicketsInHand} wicket${wicketsInHand !== 1 ? "s" : ""}`;
      }
      if (r1 > r2) {
        if (chaseNotStarted && !winnerOk) return "";
        const name = bat1 || (winnerOk ? winnerName : "");
        if (!name) return "";
        return `${name} won by ${r1 - r2} runs`;
      }
      if (chaseNotStarted && !winnerOk) return "";
      return "Match tied";
    };

    const matchTitleObj = scoreboard.find((item) => item.name === "matchtitle");
    const game_phaseObj = scoreboard.find((item) => item.name === "game_phase");
    const local_timeObj = scoreboard.find((item) => item.name === "local_time");
    const channelObj = scoreboard.find((item) => item.name === "channel");
    const homeObj = scoreboard.find((item) => item.name === "home");
    const awayObj = scoreboard.find((item) => item.name === "away");
    const battingObj = scoreboard.find(
      (item) => item.name === "Current Batting Team"
    );
    const commentObj = scoreboard.find((item) => item.name === "match_comment");
    const winnerObj = scoreboard.find((item) => item.name === "winner");
    let comment = commentObj?.value?.trim?.() ?? "";
    const winner = winnerObj?.value?.trim?.() ?? "";

    const gamePhase = game_phaseObj?.value ?? "";
    const isRrrComment = isCricketRrrComment(comment);
    // Clear a false finished latch from misreading "Innings Complete" as match over.
    const phaseLower = String(gamePhase).toLowerCase();
    const clearlyStillLivePhase =
      /\binnings?\s+complete\b/.test(phaseLower) ||
      phaseLower.includes("match in progress") ||
      phaseLower.includes("in play");
    if (
      clearlyStillLivePhase &&
      matchFinishedLatchRef.current &&
      !isMatchFinishedStatus(eventStatusRef.current) &&
      // VOID still means the fixture is decided (abandoned); only NA/empty can unlatch.
      (!winner || winner.toUpperCase() === "NA")
    ) {
      matchFinishedLatchRef.current = false;
    }
    const isFinished = resolveCricketMatchFinished({
      eventStatus: eventStatusRef.current,
      gamePhase,
      winner,
      matchComment: comment,
    });
    if (isFinished) {
      matchFinishedLatchRef.current = true;
    }
    // Finished matches: drop chase / scoreline commentary (e.g. "Surrey are 156 for 7 after 19.6 overs.")
    // so we can show the who-won line instead.
    if (isFinished && (isRrrComment || !commentIndicatesCricketMatchResult(comment))) {
      comment = "";
    }

    let matchCommentToSet = comment || (isDecimalTeamWinner(winner) ? `${winner} wins.` : "");
    if (isFinished) {
      // Prefer game_phase / status "Match Complete - X won by …" — scoreboard runs are often
      // incomplete on completed deltas, and live match_comment is frequently chase commentary.
      const fromPhase =
        resultCommentFromEventStatus(gamePhase) ||
        resultCommentFromEventStatus(eventStatusRef.current);
      const derivedWinText = deriveWinTextFromScoreboard();
      if (fromPhase) matchCommentToSet = fromPhase;
      else if (derivedWinText) matchCommentToSet = derivedWinText;
    }
    guardedSetMatchComment(matchCommentToSet);

    if (local_timeObj) {
      let localdt = new Date(local_timeObj.value);
      let formattedDate =
        localdt.getDate().toString().padStart(2, "0") +
        "/" +
        (localdt.getMonth() + 1).toString().padStart(2, "0") +
        "/" +
        localdt.getFullYear();
      setLocalTime(formattedDate);
    }

    let matchname = matchTitleObj ? matchTitleObj.value : " , , ";
    const parts = matchname.split(",");
    setMatchName((parts[1] ?? "").trim());
    setLocation((parts[2] ?? "").trim()); //setTournament((parts[0] ?? "").trim())
    setGamePhase((prev) => {
      const next = game_phaseObj?.value;
      if (next == null || String(next).trim() === "") return prev;
      return String(next);
    });
    applyTeamsFromFeed(homeObj ? homeObj.value : "", awayObj ? awayObj.value : "");
    setBatting(battingObj ? battingObj.value : "");

    const CurInns = scoreboard.find((item) => item.name === "Current Innings");
    const CurOvrs = scoreboard.find(
      (item) => item.name === "Current Innings Overs"
    );
    const CurPart = scoreboard.find(
      (item) => item.name === "Current Innings Part Overs"
    );
    // Preserve prior CurPos when a thin scoreboard frame omits overs fields —
    // resetting to [0,0,0] falsely opens Squares overs 1–2 mid-innings / on break.
    setCurPos((prev) => {
      const next = [
        CurInns ? CurInns.value : (prev?.[0] ?? 0),
        CurOvrs ? CurOvrs.value : (prev?.[1] ?? 0),
        CurPart ? CurPart.value : (prev?.[2] ?? 0),
      ];
      if (
        prev &&
        prev.length >= 3 &&
        prev[0] === next[0] &&
        prev[1] === next[1] &&
        prev[2] === next[2]
      ) {
        return prev;
      }
      return next;
    });
    setCurrentInnings(CurInns ? CurInns.value : 0);
    const maxOvers = scoreboard.find((item) => item.name === "Max Overs");
    if (maxOvers?.value != null && String(maxOvers.value).trim() !== "") {
      const liveMax = parseCricketFixtureFormatOvers(maxOvers.value);
      if (liveMax != null) setScoreboardMaxOvers(liveMax);
    }
    const reduced = scoreboardOversReduced(scoreboard);
    if (reduced != null) setOversReduced(reduced);
    const batting1 = scoreboard.find((item) => item.name === "batting.1");
    const batting1Runs = scoreboard.find((item) => item.name === "runs.1");
    const batting1Wkts = scoreboard.find((item) => item.name === "wkts.1");
    const batting1MaxOvers = scoreboard.find((item) => item.name === "max.1");
    const batting1Overs = scoreboard.find((item) => item.name === "overs.1");
    const batting2 = scoreboard.find((item) => item.name === "batting.2");

    const batting2Wkts = scoreboard.find((item) => item.name === "wkts.2");
    const batting2MaxOvers = scoreboard.find((item) => item.name === "max.2");
    const batting2Overs = scoreboard.find((item) => item.name === "overs.2");
    const batting3Overs = scoreboard.find((item) => item.name === "overs.3");
    const batting4Overs = scoreboard.find((item) => item.name === "overs.4");
    const batting2Runs = scoreboard.find((item) => item.name === "runs.2");
    let battingScore = "0/0";

    if (CurInns && CurOvrs) {
      const curInnsRunsItem = scoreboard.filter((item) =>
        String(item?.name ?? "").includes(`runs.${CurInns.value}`)
      );
      if (curInnsRunsItem && curInnsRunsItem.length > 0) {
        const curInnsPartOvers = scoreboard.find(
          (item) => item.name === "Current Innings Part Overs"
        );
        const curInnsRuns = parseInt(curInnsRunsItem[0].value);
        const curInnsOvers = parseInt(CurOvrs.value);
        const currInnsPart = parseInt(curInnsPartOvers.value);
        const realOvers = (curInnsOvers * 6 + currInnsPart) / 6;
        setRunRate((curInnsRuns / realOvers).toFixed(2));
      }
    }

    let b1mo = batting1MaxOvers ? batting1MaxOvers.value : 0;
    let b2mo = batting2MaxOvers ? batting2MaxOvers.value : 0;
    let b1o = batting1Overs ? batting1Overs.value : 0;
    let b2o = batting2Overs ? batting2Overs.value : 0;
    let b3o = batting2Overs ? batting3Overs.value : 0;
    let b4o = batting2Overs ? batting4Overs.value : 0;
    setBattingOvers([b1o, b2o, b3o, b4o]);

    let b1r = batting1Runs ? batting1Runs.value : 0;
    let b2r = batting2Runs ? batting2Runs.value : 0;
    let b1w = batting1Wkts ? batting1Wkts.value : 0;
    let b2w = batting2Wkts ? batting2Wkts.value : 0;

    let teambowl = "",
      teambat = "";

    setMOvers([b1mo, b2mo]);
    let homeOver = b1o + "/" + b1mo;
    if (b1o === b1mo) {
      homeOver = b1mo;
    }
    let awayOver = b2o + "/" + b2mo;
    if (b2o === b2mo) {
      awayOver = b2mo;
    }
    if (awayOver === "") {
      awayOver = "0/" + (maxOvers?.value ?? "");
    }
    let bat1Prog = b1r + "/" + b1w;
    if (bat1Prog === "/") {
      bat1Prog = "0/0";
    }
    let bat2Prog = b2r + "/" + b2w;
    if (bat2Prog === "/") {
      bat2Prog = "0/0";
    }
    let batx1 = batting1 ? batting1.value : "";
    if (batx1 === (homeObj?.value ?? "")) {
      guardedSetScore([bat1Prog, bat2Prog]);
      setOvers([homeOver, awayOver]);
      battingScore = bat1Prog;
      teambat = awayObj?.value ?? "";
      teambowl = homeObj?.value ?? "";
    } else {
      guardedSetScore([bat2Prog, bat1Prog]);
      setOvers([awayOver, homeOver]);
      battingScore = bat2Prog;
      teambat = awayObj?.value ?? "";
      teambowl = homeObj?.value ?? "";
    }

    let batv = battingObj ? battingObj.value : "";
    const bat1 = scoreboard.find((item) => item.name === batv + "Bat.1");
    const bat2 = scoreboard.find((item) => item.name === batv + "Bat.2");
    // Decimal API Appendix 6: "Batsman1 Strike" / "Batsman2 Strike" = 0 or 1 indicate who is on strike
    const strike1 = scoreboard.find((item) => item.name === "Batsman1 Strike");
    let bowl1 = scoreboard.find((item) => item.name === "bowler1name");
    let bowl2 = scoreboard.find((item) => item.name === "bowler2name");
    const runs1 = scoreboard.find((item) => item.name === "Batsman1 Runs");
    const runs2 = scoreboard.find((item) => item.name === "Batsman2 Runs");
    let bowl1overs = scoreboard.find((item) => item.name === "bowler1overs");
    let bowl1runs = scoreboard.find((item) => item.name === "bowler1runs");
    let bowl1wicks = scoreboard.find((item) => item.name === "bowler1wickets");
    let bowl2overs = scoreboard.find((item) => item.name === "bowler2overs");
    let bowl2runs = scoreboard.find((item) => item.name === "bowler2runs");
    let bowl2wicks = scoreboard.find((item) => item.name === "bowler2wickets");
    const balls1 = scoreboard.find((item) => item.name === batv + "Bat.1balls");
    const balls2 = scoreboard.find((item) => item.name === batv + "Bat.2balls");
    // Current batter = who is on strike per API: Batsman1 Strike "1" => Bat.1, else Bat.2
    const isBatsman1OnStrike = (strike1?.value ?? "1") === "1";
    const strikerbatter = isBatsman1OnStrike ? (bat1?.value ?? "") : (bat2?.value ?? "");
    const batterruns = isBatsman1OnStrike ? (runs1?.value ?? 0) : (runs2?.value ?? 0);
    const batterballs = isBatsman1OnStrike ? (balls1?.value ?? 0) : (balls2?.value ?? 0);
    setStriker(strikerbatter);
    let actbowl = scoreboard.find((item) => item.name === "bowler");
    let bowlovers = "0 overs";
    let bowlruns = "0";
    let bowlwickets = "0";
    if (bowl1 ? bowl1.value : "" !== "") {
      actbowl = bowl1.value;
      bowlovers = bowl1overs.value;
      bowlruns = bowl1runs.value;
      bowlwickets = bowl1wicks.value;
    } else {
      actbowl = bowl2 ? bowl2.value : "";
      bowlovers = bowl2overs ? bowl2overs.value : "";
      bowlruns = bowl2runs ? bowl2runs.value : "";
      bowlwickets = bowl2wicks ? bowl2wicks.value : "";
    }
    setBowling(actbowl);
    setPartnership({
      bowler: actbowl,
      bowlerover: bowlovers,
      bowlerruns: bowlruns,
      bowlerwickets: bowlwickets,
      bowler2: bowl2 ? bowl2.value : "",
      bowlerover2: bowl2overs ? bowl2overs.value : "",
      bowlerruns2: bowl2runs ? bowl2runs.value : "",
      bowlerwickets2: bowl2wicks ? bowl2wicks.value : "",
      bowlteam: teambowl,
      batteam: teambat,
      batter: strikerbatter,
      batterruns: batterruns,
      batterballs: batterballs,
      batter2: bat2?.value ? bat2.value : "",
      batterruns2: runs2?.value ? runs2.value : "",
      batterballs2: balls2?.value ? balls2.value : "",
    }); /** feed events */

    if (Array.isArray(event.score) && event.score.length > 0) {
      setBallResults(ballResultsFromEventScore(event.score));
      const scoreIdx = event.score.findIndex((itm) => itm && itm.priority === 1);
      const scItem = scoreIdx >= 0 ? event.score[scoreIdx] : null;
      if (scItem && scItem.result != null) {
        const tEvent = String(scItem.result);
        if (
          tEvent.substring(0, 6).toUpperCase() === "WICKET" ||
          tEvent.includes("6 runs") ||
          tEvent.includes("4 runs")
        ) {
          setIncomingEvent(true);
          if (matchId) {
            setTimeout(() => {
              fetchEvents(matchId);
            }, 2000);
          }
        }
      }
    }
  }, []);

  const fetchEvents = useCallback(async (match_id) => {
    try {
      const response = await fetch(
        `${process.env.API_URL}/events/${match_id}`,
        { method: "GET", headers: getAuthHeaders() }
      );
      if (response.status === 200) {
        const data = await response.json();
        setStream(data.stream.trim());
        if (data.stream.trim() === "2") {
          setSubHeader("stream");
          if (embedcode === "") {
            getEmbedCode(match_id);
          }
        }
        setFeed(data.data);
        setIncomingEvent(false);
      }
    } catch (error) {
      return null;
    }
  }, [embedcode, getEmbedCode]);

  const fetchScoreGrid = useCallback(async (match_id, scoreGridData) => {
    const data = scoreGridData;
    const overs = data.overs;

    let scoreDisplay: (string | number)[] = [];
    for (let inns = 1; inns <= 4; inns++) {
      const currentInns = overs[`inns${inns}`];
      if (currentInns) {
        currentInns.forEach((over) => {
          let ignoreOver = true;
          for (let i = 1; i <= 12; i++) {
            const ballKey = `b${i}`;
            const ballValue = over[ballKey];
            if (ballValue !== null && ballValue !== "") {
              ignoreOver = false;
              scoreDisplay.push(ballValue);
            }
          }
          if (!ignoreOver) {
            scoreDisplay.push("OVER " + over.ov);
          }
        });
      }
    }
    setScoregrid(scoreDisplay.reverse());
  }, []);

  const applyScoregridFromParsed = useCallback(
    (matchId: string, parsedData: any) => {
      const sg = parsedData?.scoregrid;
      if (!sg || typeof sg !== "object" || !sg.overs) return;
      const v = parseLastUpdateVersion(parsedData.last_update);
      const map = latestScoregridLastUpdatePerMatchRef.current;
      const prevSg = map.get(matchId);
      if (v != null && prevSg != null && v < prevSg) {
        const incoming = extractLatestBowlingRowFromScoregrid(sg);
        const curSg = scoreGridDataRef.current as any;
        const current =
          curSg && typeof curSg === "object" && curSg.overs
            ? extractLatestBowlingRowFromScoregrid(curSg)
            : null;
        if (
          incoming &&
          current &&
          incoming.inns === current.inns &&
          Number(incoming.ov) === Number(current.ov)
        ) {
          const incN = filterToCountedBallResults(incoming.delivery).length;
          const curN = filterToCountedBallResults(current.delivery).length;
          if (incN > curN) {
            setSquaresStaleScoregridBallSupplement({
              matchId: String(matchId),
              inns: incoming.inns,
              ov: incoming.ov,
              delivery: incoming.delivery,
            });
            // Keep the live delivery strip in sync; otherwise Squares "over in play" only saw bumps via
            // supplement merge and `overDelivery` could stay stale until a full non-stale scoregrid apply.
            setOverDelivery(incoming.delivery);
          }
        }
        if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
          console.warn(
            `[Stream] Ignoring stale scoregrid for match ${matchId}: incoming=${v}, latestScoregrid=${prevSg}`
          );
        }
        return;
      }
      setSquaresStaleScoregridBallSupplement((prev) =>
        prev != null && String(prev.matchId) === String(matchId) ? null : prev
      );
      if (v != null) map.set(matchId, v);
      setScoreGridData(sg);
      fetchScoreGrid(matchId, sg);
      let found = false;
      for (let i = 4; i >= 1 && !found; i--) {
        const thisInns = sg.overs["inns" + i];
        if (!Array.isArray(thisInns) || thisInns.length === 0) continue;
        for (let ov = thisInns.length - 1; ov >= 0; ov--) {
          const row = thisInns[ov];
          if (row && row.bowler !== "" && row.bowlerid !== "") {
            const delivery = [
              row.b1,
              row.b2,
              row.b3,
              row.b4,
              row.b5,
              row.b6,
              row.b7,
              row.b8,
              row.b9,
              row.b10,
              row.b11,
              row.b12,
            ];
            if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
              const key = `${matchId}:${i}:${row.ov}`;
              const counted = filterToCountedBallResults(delivery).length;
              const prevCounted = devLastCountedBallsRef.current;
              const delta = prevCounted?.key === key ? counted - prevCounted.counted : 0;
              const unusualToken = delivery.some((b) => {
                const s = String(b ?? "").trim();
                return s !== "" && !/^(\d|W|\d(lb|b|w|nb)|W\+\d)$/i.test(s);
              });
              if (delta > 1 || delta < 0 || (delta !== 0 && unusualToken)) {
                console.warn(
                  `[Squares] Counted balls ${prevCounted?.counted} -> ${counted} for ${key}; raw b1..b12:`,
                  delivery
                );
              }
              devLastCountedBallsRef.current = { key, counted };
            }
            setOverDelivery(delivery);
            found = true;
            break;
          }
        }
      }
    },
    [fetchScoreGrid, parseLastUpdateVersion]
  );

  const subscribeToMatches = useCallback((
    matchIds,
    feedFilter,
    cb?: (path: string, options: { replace: boolean }) => void
  ) => {
    // Guard against undefined/null matchIds or empty array
    if (!matchIds || !Array.isArray(matchIds) || matchIds.length === 0) {
      console.warn("subscribeToMatches called with invalid matchIds:", matchIds);
      return () => {}; // Return a no-op unsubscribe function
    }
    
    // Filter out any undefined/null values from matchIds
    const validMatchIds = matchIds.filter(id => id != null && id !== 'undefined' && id !== 'null');
    if (validMatchIds.length === 0) {
      console.warn("subscribeToMatches: no valid matchIds after filtering");
      return () => {};
    }

    const wantMarkets = feedWantsMarkets(feedFilter);
    
    const baseApi = process.env.STREAM_API_URL;
    const url = `${baseApi}?matchIds=${validMatchIds.join(',')}`;
    const es = new EventSource(url, { withCredentials: false });

    es.addEventListener("open", () => {
      validMatchIds.forEach((id) => clearMatchFeedVersionGuards(String(id)));
    });

    es.addEventListener("message", async function incoming(event) {
      let parsedData;
      try {
        parsedData = JSON.parse((event as MessageEvent).data as string);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'prod') {
          console.error('Could not parse event data', e.message);
        }
        return;
      }
      if (!parsedData) {
        return;
      }
      if (parsedData.market_update) {
        parsedData = {
          ...parsedData,
          market_update: unwrapStreamMarketUpdate(parsedData.market_update),
        };
      }
      // If feed_id is missing, we can't safely process this event for multi-match subscriptions
      const currentMatchIdForEvent = parsedData.feed_id;
      if (!currentMatchIdForEvent) {
        // If feed_id is missing and we're subscribed to multiple matches, skip this event
        // to avoid associating data with the wrong match
        if (validMatchIds.length > 1) {
          if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'prod') {
            console.warn('Event missing feed_id, skipping to avoid wrong match association:', parsedData);
          }
          return;
        }
        // Only use fallback if we're subscribed to a single match (safe assumption)
        if (validMatchIds.length === 1) {
          // Still log a warning as feed_id should always be present
          if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'prod') {
            console.warn('Event missing feed_id, using single subscribed matchId as fallback');
          }
        } else {
          // No valid matchIds, can't process
          return;
        }
      }
      // Use feed_id if available, otherwise fallback to single matchId (only safe when length === 1)
      const matchIdToUse = currentMatchIdForEvent || validMatchIds[0];
      const mid = String(matchIdToUse);
      const lu = parsedData.last_update;
      applyScoregridFromParsed(mid, parsedData);

      const skipCricket = isStaleCricketFeedVersion(mid, lu);
      const skipScorecard = isStaleScorecardFeedVersion(mid, lu);
      const rawMarketsMulti = parsedData.market_update?.markets ?? [];
      const marketLuStale = isStaleMarketFeedVersion(mid, lu);
      const carrySettledMarketUpdates = rawMarketsMulti.some((m: unknown) =>
        liveMarketRowIndicatesFinalSettlement(m)
      );
      const skipMarket =
        rawMarketsMulti.length > 0 && marketLuStale && !carrySettledMarketUpdates;

      if (skipCricket && (parsedData.event || parsedData.scoreboard)) {
        if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
          const v = parseLastUpdateVersion(lu);
          const prev = latestCricketFeedVersionPerMatchRef.current.get(mid);
          console.warn(
            `[Stream] Skipping stale cricket snapshot for ${mid}: incoming=${v}, latestCricket=${prev}`,
            { msg_type: parsedData.msg_type, hasEvent: Boolean(parsedData.event), hasScorecard: Boolean(parsedData.scorecard) }
          );
        }
      }
      if (skipScorecard && parsedData.scorecard) {
        if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
          const v = parseLastUpdateVersion(lu);
          const prev = latestScorecardLastUpdatePerMatchRef.current.get(mid);
          console.warn(
            `[Stream] Skipping stale scorecard for ${mid}: incoming=${v}, latestScorecard=${prev}`
          );
        }
      }
      if (skipMarket && rawMarketsMulti.length > 0) {
        if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
          const v = parseLastUpdateVersion(lu);
          const prev = latestMarketFeedVersionPerMatchRef.current.get(mid);
          console.warn(`[Stream] Skipping stale market snapshot for ${mid}: incoming=${v}, latestMarket=${prev}`);
        }
      }

      // ----------------------------- market_update -------------------------
      // Full books use market_snapshot (replace/evict). Empty markets (e.g. global_betting_status off) do not overwrite.
      // `settled_market_keys` deletes Appendix-4 finals that SSE omits from `markets[]`.
      if (parsedData.market_update) {
        if (parsedData.market_update.event_status != null) {
          applyEventStatusFromFeed(parsedData.market_update.event_status);
        }
        if (parsedData.market_update.betting_status != null) {
          setMatchTradingStatus(parsedData.market_update.betting_status);
        }
        const rawMarkets = parsedData.market_update.markets ?? [];
        if (!skipMarket && wantMarkets) {
          let bookChanged = false;
          if (rawMarkets.length > 0) {
            const fullSnapshot = streamMessageIsMarketSnapshot(parsedData);
            if (marketLuStale && carrySettledMarketUpdates) {
              applyMarketsFromStreamMessage(marketDataMapRef.current, rawMarkets, {
                staleSettledOnly: true,
              });
            } else {
              applyMarketsFromStreamMessage(marketDataMapRef.current, rawMarkets, {
                fullSnapshot,
              });
            }
            bookChanged = true;
          }
          if (applySettledMarketKeyRemovals(marketDataMapRef.current, parsedData)) {
            bookChanged = true;
          }
          if (bookChanged) scheduleMarketDataFlush();
          if (rawMarkets.length > 0) {
            applyWinProbabilityUpdate(parsedData, skipCricket);
          }
        }
      }

      if (parsedData.event && !skipCricket) {
        if (String(parsedData.event.event_description ?? "").includes("Pre Match Service")) {
          setPrematch(true);
          const preMatchScoreboard = eventScoreboardRows(parsedData.event);
          if (preMatchScoreboard.length > 0) {
            const scoreboard = preMatchScoreboard;
            const homeObj = scoreboard.find((item) => item.name === "home");
            const awayObj = scoreboard.find((item) => item.name === "away");
            applyTeamsFromFeed(
              homeObj ? homeObj.value : "",
              awayObj ? awayObj.value : ""
            );
          }
          setGamePhase((prev) => (prev ? prev : "Scheduled"));
        } else {
          setPrematch(false);
          const scoreboard = eventScoreboardRows(parsedData.event);
          if (scoreboard.length === 0) {
            // Slim lobby event frames may carry status without scoreboard rows.
          } else {
          const matchTitleObj = scoreboard.find(
            (item) => item.name === "matchtitle"
          );
          const game_phaseObj = scoreboard.find(
            (item) => item.name === "game_phase"
          );
          const local_timeObj = scoreboard.find(
            (item) => item.name === "local_time"
          );
          const homeObj = scoreboard.find((item) => item.name === "home");
          const awayObj = scoreboard.find((item) => item.name === "away");
          const battingObj = scoreboard.find(
            (item) => item.name === "Current Batting Team"
          );

          let localdt = new Date(local_timeObj ? local_timeObj.value : "");
          let formattedDate =
            localdt.getDate().toString().padStart(2, "0") +
            "/" +
            (localdt.getMonth() + 1).toString().padStart(2, "0") +
            "/" +
            localdt.getFullYear();

          let matchname = matchTitleObj ? matchTitleObj.value : "";
          const parts = matchname.split(",");
          if (parts.length > 2) {
            setMatchName(parts[0].trim());
            setLocation(parts[2].trim());
            setTournament(parts[1].trim());
          }
          setGamePhase((prev) => {
            const next = game_phaseObj?.value;
            if (next == null || String(next).trim() === "") return prev;
            return String(next);
          });
          setLocalTime(formattedDate);
          applyTeamsFromFeed(
            homeObj ? homeObj.value : "",
            awayObj ? awayObj.value : ""
          );

          setBatting(battingObj ? battingObj.value : "");

          const CurInns = scoreboard.find(
            (item) => item.name === "Current Innings"
          );
          const CurOvrs = scoreboard.find(
            (item) => item.name === "Current Innings Overs"
          );
          const CurPart = scoreboard.find(
            (item) => item.name === "Current Innings Part Overs"
          );
          setCurPos((prev) => {
            const next = [
              CurInns ? CurInns.value : (prev?.[0] ?? 0),
              CurOvrs ? CurOvrs.value : (prev?.[1] ?? 0),
              CurPart ? CurPart.value : (prev?.[2] ?? 0),
            ];
            if (
              prev &&
              prev.length >= 3 &&
              prev[0] === next[0] &&
              prev[1] === next[1] &&
              prev[2] === next[2]
            ) {
              return prev;
            }
            return next;
          });

          const maxOvers = scoreboard.find((item) => item.name === "Max Overs");
          if (maxOvers?.value != null && String(maxOvers.value).trim() !== "") {
            const liveMax = parseCricketFixtureFormatOvers(maxOvers.value);
            if (liveMax != null) setScoreboardMaxOvers(liveMax);
          }
          const reduced = scoreboardOversReduced(scoreboard);
          if (reduced != null) setOversReduced(reduced);
          const batting1 = scoreboard.find((item) => item.name === "batting.1");
          const batting1Runs = scoreboard.find(
            (item) => item.name === "runs.1"
          );
          const batting1Wkts = scoreboard.find(
            (item) => item.name === "wkts.1"
          );
          const batting1MaxOvers = scoreboard.find(
            (item) => item.name === "max.1"
          );
          const batting1Overs = scoreboard.find(
            (item) => item.name === "overs.1"
          );

          const batting2 = scoreboard.find((item) => item.name === "batting.2");
          const batting2Runs = scoreboard.find(
            (item) => item.name === "runs.2"
          );
          const batting2Wkts = scoreboard.find(
            (item) => item.name === "wkts.2"
          );
          const batting2MaxOvers = scoreboard.find(
            (item) => item.name === "max.2"
          );
          const batting2Overs = scoreboard.find(
            (item) => item.name === "overs.2"
          );

          let b1mover = batting1MaxOvers ? batting1MaxOvers.value : "";
          let b2mover = batting2MaxOvers ? batting2MaxOvers.value : "";
          let b1over = batting1Overs ? batting1Overs.value : "";
          let b2over = batting2Overs ? batting2Overs.value : "";

          let homeOver = b1over + "/" + b1mover;
          if (b1over === b1mover) {
            homeOver = b1mover;
          }
          let awayOver = b2over + "/" + b2mover;

          let bat1Runs = batting1Runs ? batting1Runs.value : "";
          let bat1Wkts = batting1Wkts ? batting1Wkts.value : "";
          let bat2Runs = batting2Runs ? batting2Runs.value : "";
          let bat2Wkts = batting2Wkts ? batting2Wkts.value : "";

          if (b2over === b2mover) {
            awayOver = b2mover;
          }
          if (awayOver === "") {
            awayOver = "0/" + (maxOvers?.value ?? "");
          }
          let bat1Prog = bat1Runs + "/" + bat1Wkts;
          if (bat1Prog === "/") {
            bat1Prog = "0/0";
          }
          let bat2Prog = bat2Runs + "/" + bat2Wkts;
          if (bat2Prog === "/") {
            bat2Prog = "0/0";
          }

          if ((batting1 ? batting1.value : "") === (homeObj?.value ?? "")) {
            guardedSetScore([bat1Prog, bat2Prog]);
            setOvers([homeOver, awayOver]);
          } else {
            guardedSetScore([bat2Prog, bat1Prog]);
            setOvers([awayOver, homeOver]);
          }

          const bat1 = scoreboard.find((item) =>
            item.name === battingObj ? battingObj.value : "" + "Bat.1"
          );
          const bat2 = scoreboard.find((item) =>
            item.name === battingObj ? battingObj.value : "" + "Bat.2"
          );
          const strike1 = scoreboard.find(
            (item) => item.name === "Batsman1 Strike"
          );
          const strike2 = scoreboard.find(
            (item) => item.name === "Batsman2 Strike"
          );
          let bowl1 = scoreboard.find((item) => item.name === "bowler1name");
          let bowl2 = scoreboard.find((item) => item.name === "bowler2name");
          const runs1 = scoreboard.find(
            (item) => item.name === "Batsman1 Runs"
          );
          const runs2 = scoreboard.find(
            (item) => item.name === "Batsman2 Runs"
          );
          let bowl1overs = scoreboard.find(
            (item) => item.name === "bowler1overs"
          );
          let bowl1runs = scoreboard.find(
            (item) => item.name === "bowler1runs"
          );
          let bowl1wicks = scoreboard.find(
            (item) => item.name === "bowler1wickets"
          );
          let bowl2overs = scoreboard.find(
            (item) => item.name === "bowler2overs"
          );
          let bowl2runs = scoreboard.find(
            (item) => item.name === "bowler2runs"
          );
          let bowl2wicks = scoreboard.find(
            (item) => item.name === "bowler2wickets"
          );
          const balls1 = scoreboard.find((item) =>
            item.name === battingObj ? battingObj.value : "" + "Bat.1balls"
          );
          const balls2 = scoreboard.find((item) =>
            item.name === battingObj ? battingObj.value : "" + "Bat.2balls"
          );
          let strikerbatter = "";
          let batterruns = 0;
          let batterballs = 0;
          if (strike1 && strike1.value === "1") {
            strikerbatter = bat1 ? bat1.value : 0;
            batterruns = runs1?.value ?? 0;
            batterballs = balls1?.value ?? 0;
          } else {
            strikerbatter = bat2 ? bat2.value : 0;
            batterruns = runs2?.value ?? 0;
            batterballs = balls2?.value ?? 0;
          }

          let actbowl = scoreboard.find((item) => item.name === "bowler");
          let bowlovers = "0 overs";
          let bowlruns = "0";
          let bowlwickets = "0";
          if (bowl1?.value !== undefined && bowl1.value !== "") {
            actbowl = bowl1.value;
            bowlovers = (bowl1overs?.value ?? "") + " overs";
            bowlruns = bowl1runs?.value ?? "";
            bowlwickets = bowl1wicks?.value ?? "";
          } else {
            actbowl = bowl2?.value ?? "";
            bowlovers = (bowl2overs?.value ?? "") + " overs";
            bowlruns = bowl2runs?.value ?? "";
            bowlwickets = bowl2wicks?.value ?? "";
          }
          setPartnership({
            bowler: actbowl,
            bowlerover: bowlovers,
            bowlerruns: bowlruns,
            bowlerwickets: bowlwickets,
            batter: strikerbatter,
            batterruns: batterruns,
            batterballs: batterballs,
          });
          }
        }
      }
      // ------------ score card ------------
      if (parsedData.event && !skipCricket) {
        EventProc(parsedData.event, matchIdToUse);
      }

      // ---------------- match details -----------------
      matchDetails(parsedData, matchIdToUse, cb, {
        skipCricketSnapshot: skipCricket,
        skipMarketData: skipMarket,
        skipScorecard: skipScorecard,
        wantMarkets,
      });

      //-------------------------- match-play -----------------------------
      if (parsedData.event && !skipCricket) {
        const eventScoreboard = eventScoreboardRows(parsedData.event);
        const winner =
          eventScoreboard.find((item) => item.name === "winner")
            ?.value ?? "NA";
        const curOvs =
          eventScoreboard.find(
            (item) => item.name === "Current Innings Overs"
          )?.value ?? "";
        const curDelv =
          eventScoreboard.find(
            (item) => item.name === "current.delv"
          )?.value ?? "";
        const matchComment =
          eventScoreboard.find(
            (item) => item.name === "match_comment"
          )?.value ?? "";
        const gamePhase =
          eventScoreboard.find((item) => item.name === "game_phase")
            ?.value ?? "";
        const currentBowler =
          eventScoreboard.find((item) => item.name === "bowler")
            ?.value ?? "";
        const currentBatting =
          eventScoreboard.find(
            (item) => item.name === "Current Batting Team"
          )?.value ?? "";
        const batsman1 =
          eventScoreboard.find(
            (item) => item.name === currentBatting + "Bat.1"
          )?.value ?? "";
        const batsman2 =
          eventScoreboard.find(
            (item) => item.name === currentBatting + "Bat.2"
          )?.value ?? "";

        const strike1 = eventScoreboard.find(
          (item) => item.name === "Batsman1 Strike"
        );
        const strike2 = eventScoreboard.find(
          (item) => item.name === "Batsman2 Strike"
        );
        let ballStatus = "";
        if ((strike1?.value ?? "1") === "1") {
          ballStatus = currentBowler + " to " + batsman1;
        } else if ((strike2?.value ?? "1") === "1") {
          ballStatus = currentBowler + " to " + batsman2;
        }
        if (!isDecimalTeamWinner(winner)) {
          setStatus(ballStatus);
          setCurBall(curOvs + "." + curDelv);
          setCurInnsOver(curOvs);
        } else {
          setStatus(winner + " wins.");
          setCurBall(null);
        }
      }

      if (parsedData.commentary) {
        setCommentary((prevC) => mergeCommentariesIntoState(prevC, parsedData.commentary));
      }
      if (parsedData.scoreboard && !skipCricket) {
        setBattings([
          parsedData.scoreboard.innings1battingteam,
          parsedData.scoreboard.innings2battingteam,
          parsedData.scoreboard.innings3battingteam,
          parsedData.scoreboard.innings4battingteam,
        ]);
        let stat = parsedData.scoreboard?.status.split("-")[0];
        if (
          parsedData.scoreboard.innings1battingteam ===
          parsedData.scoreboard.home
        ) {
          setHomeOvers([
            parsedData.scoreboard.innings1overs,
            parsedData.scoreboard.innings3overs,
          ]);
          setAwayOvers([
            parsedData.scoreboard.innings2overs,
            parsedData.scoreboard.innings4overs,
          ]);
        } else {
          setAwayOvers([
            parsedData.scoreboard.innings1overs,
            parsedData.scoreboard.innings3overs,
          ]);
          setHomeOvers([
            parsedData.scoreboard.innings2overs,
            parsedData.scoreboard.innings4overs,
          ]);
        }
        setMOvers([
          parsedData.scoreboard.innings1max,
          parsedData.scoreboard.innings2max,
          parsedData.scoreboard.innings3max,
          parsedData.scoreboard.innings4max,
        ]);
      }
      if (parsedData.scorecard && !skipScorecard) {
        const cardMap = mergedScorecardByMatchIdRef.current;
        const mergedSc = mergeDecimalScorecardDelta(cardMap.get(mid) ?? null, parsedData.scorecard) as any;
        cardMap.set(mid, mergedSc);

        if (typeof mergedSc?.description === "string" && mergedSc.description.includes(",")) {
          const matchParts = mergedSc.description.split(",");
          if (matchParts[0]?.includes(" v ")) {
            setMatchTitle(matchParts.length >= 3 ? matchParts[1].trim() : "");
            applyTeamsFromMatchTitlePart(matchParts[0]);
          }
        }

        setInnsNow(mergedSc.inns_now);
        setBatNow(mergedSc.bat_now);
        const mp = typeof mergedSc?.description === "string" ? mergedSc.description.split(",") : [];
        if (mp[0]?.includes(" v ")) {
          const s1 = inningRunsWktsLine(mergedSc.inns1);
          const s2 = inningRunsWktsLine(mergedSc.inns2);
          const s3 = inningRunsWktsLine(mergedSc.inns3);
          const s4 = inningRunsWktsLine(mergedSc.inns4);
          const insScore = [s1, s2, s3, s4];
          const innsnow = mergedSc.inns_now;
          let oppnow = 1;
          let rptNow = 2;
          if (innsnow === 1 || innsnow === 3) {
            oppnow = 2;
            rptNow = 1;
          }
          setLiveBat(mergedSc.bat_now);
          if (mergedSc.bat_now === mp[0].split(" v ")[0]) {
            guardedSetScore([
              insScore[rptNow - 1],
              insScore[oppnow - 1],
              insScore[rptNow + 1],
              insScore[oppnow + 1],
            ]);
          } else {
            guardedSetScore([
              insScore[oppnow - 1],
              insScore[rptNow - 1],
              insScore[oppnow + 1],
              insScore[rptNow + 1],
            ]);
          }
        }
        setScorecardData(mergedSc);
        if (mergedSc.event_status != null) {
          applyEventStatusFromFeed(mergedSc.event_status);
        }
      }
      if (parsedData.alerts) {
        if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
          console.log("[Alerts] received (stream 2):", JSON.stringify(parsedData.alerts, null, 2));
        }
        setAlerts(parsedData.alerts);
      }
      const battingAnalysisMsg = battingAnalysisFromMessage(parsedData);
      if (battingAnalysisMsg) setBattingAnalysis(battingAnalysisMsg);

      if (!skipCricket && (parsedData.event || parsedData.scoreboard)) {
        bumpCricketFeedVersion(mid, lu);
      }
      if (parsedData.scorecard && !skipScorecard) {
        bumpScorecardFeedVersion(mid, lu);
      }
      // Advance market monotonic guard on every non-stale market_update tick, including empty `markets`
      // (suspend / metadata-only). Otherwise a newer empty message never bumps the version and a slightly
      // older full list can be skipped as "stale", leaving removed markets on screen until refresh.
      if (parsedData.market_update && parseLastUpdateVersion(lu) != null && !marketLuStale) {
        bumpMarketFeedVersion(mid, lu);
      }
      //-------------------- match-play --------------------------

      //---------------------- trader-match-card ------------------------
      //----------------------- trader-match-card --------------------------
    });
    es.addEventListener("error", function error(ev) {
      // SSE error; browser may auto-retry. Logging for diagnostics.
      // console.error('SSE stream error', ev);
    });
    return () => {
      es.close();
    };
  }, [
    scheduleMarketDataFlush,
    applyWinProbabilityUpdate,
    EventProc,
    matchDetails,
    fetchScoreGrid,
    applyScoregridFromParsed,
    isStaleCricketFeedVersion,
    isStaleMarketFeedVersion,
    isStaleScorecardFeedVersion,
    bumpCricketFeedVersion,
    bumpMarketFeedVersion,
    bumpScorecardFeedVersion,
    clearMatchFeedVersionGuards,
    parseLastUpdateVersion,
  ]);

  const subscribeToMatch = useCallback((
    matchId,
    feedFilter,
    traderTeams,
    cb?: (path: string, options: { replace: boolean }) => void,
    product?: string,
    tier?: string,
    book?: string
  ) => {
    /** Trader trades live Decimal prices, so it opts into the server-side house odds margin (`product=trader`). */
    const productQuery = String(product ?? "").toLowerCase() === "trader" ? "trader" : "";
    /**
     * `tier=lobby` slims the feed (no commentary / scoregrid / lineups / full scorecard), except
     * `book=catalog` logged-out Trader match play which still sends live cricket chrome.
     * `book=catalog` (logged-out Trader match play) still sends every market, with live prices
     * only on Match Winner.
     */
    const tierQuery = String(tier ?? "").toLowerCase() === "lobby" ? "lobby" : "";
    const bookQuery = String(book ?? "").toLowerCase() === "catalog" ? "catalog" : "";
    /** Combined `&`-prefixed query for the `initial-state` endpoint (which already has `matchIds`). */
    const initialStateExtraQuery = `${productQuery ? `&product=${productQuery}` : ""}${tierQuery ? `&tier=${tierQuery}` : ""}${bookQuery ? `&book=${bookQuery}` : ""}`;
    /** Full `?`-prefixed query for the single-match SSE URL. */
    const streamUrlQuery = [productQuery && `product=${productQuery}`, tierQuery && `tier=${tierQuery}`, bookQuery && `book=${bookQuery}`]
      .filter(Boolean)
      .join("&");
    const streamUrlSuffix = streamUrlQuery ? `?${streamUrlQuery}` : "";
    // Guard against undefined/null matchId
    if (!matchId) {
      console.warn("subscribeToMatch called with undefined/null matchId");
      return () => {}; // Return a no-op unsubscribe function
    }

    /** Respect `feedFilter`: Bullion played view omits `market_update` — no live order-book merge; odds come from the frozen Bullion `odds_snapshot`. Match-winner win% still runs via `applyWinProbabilityUpdate` when payloads include MW rows. */
    const wantMarkets = feedWantsMarkets(feedFilter);
    
    // Clear all match-specific state when switching to a new match (only if matchId changed)
    const matchIdStr = String(matchId);
    if (currentMatchIdRef.current !== matchIdStr) {
      marketDataMapRef.current.clear();
      clearMatchFeedVersionGuards(matchIdStr);
      setMarketData([]);
      setSubscribedMatchId(matchIdStr);
      // Reset tournament and matchTitle when switching to a new match
      setTournament("");
      setMatchTitle("");
      // Reset scorecard, commentary, ball runs, RR and all scoreboard state so previous match data is not shown
      mergedScorecardByMatchIdRef.current.clear();
      setScorecardData(null as any);
      setCommentary({});
      matchFinishedLatchRef.current = false;
      matchCommentRef.current = "";
      setMatchComment("");
      setRunRate("");
      scoreRef.current = ["0/0", "0/0"];
      setScore(["0/0", "0/0"]);
      setOvers([""]);
      setLiveBat("");
      setInnsNow(0);
      setBatNow("");
      setOverDelivery([]);
      setCurBall(null);
      setCurInnsOver("");
      setScoregrid([]);
      setScoreGridData([]);
      setBattings([]);
      setHomeOvers([]);
      setAwayOvers([]);
      setMOvers([]);
      setCurPos([0, 0, 0]);
      setFormat(null);
      setScoreboardMaxOvers(null);
      setOversReduced(false);
      setBattingOvers([0, 0, 0, 0]);
      setPartnership({});
      setBatting(null);
      setStriker("");
      setBowling(undefined);
      setFullComm([]);
      setLineup([]);
      setCompetitionGender(undefined);
      setAlerts(null);
      setBattingAnalysis(null);
      setBallResults(null);
      setCurrentInnings(0);
      setPrematch(true);
      prematchRef.current = true;
      setProbability([
        { name: "", probability: 0 },
        { name: "", probability: 0 },
      ]);
      setGamePhase("");
      setStatus("");
      eventStatusRef.current = undefined;
      setEventStatus(undefined);
      setMatchTradingStatus(undefined);
      setSubscriptionState(undefined);
      setSquaresOversRefreshNonce(0);
      setBullionSettlementRefresh({ nonce: 0, matchId: "", gameIds: [] });
      setSquaresStaleScoregridBallSupplement(null);
      liveDetailHydrateDoneRef.current.delete(matchIdStr);
      currentMatchIdRef.current = matchIdStr;
    }

    const baseApi = process.env.STREAM_API_URL;
    
    const existingShared = sharedSingleMatchSubscriptionRef.current;
    if (existingShared && existingShared.matchId === matchIdStr) {
      existingShared.subscribers += 1;
      setConnectionLost(false);
      // Shared SSE will not re-replay cache for late joiners — hydrate via HTTP snapshot.
      const streamBase = (baseApi || "").replace(/\/stream.*$/i, "");
      if (streamBase) {
        fetch(
          `${streamBase}/stream/initial-state?matchIds=${encodeURIComponent(matchIdStr)}${initialStateExtraQuery}`,
          { credentials: "omit" }
        )
          .then((r) => (r.ok ? r.json() : null))
          .then((state: Record<string, unknown> | null) => {
            if (!state || typeof state !== "object") return;
            const snap = state[matchIdStr] as Record<string, unknown> | undefined;
            if (!snap || typeof snap !== "object") return;
            matchDetails(snap, matchId, cb, { wantMarkets });
            if (snap.commentary) {
              setCommentary((prevC) => mergeCommentariesIntoState(prevC, snap.commentary as any));
            }
            if (snap.scorecard) {
              const cardMap = mergedScorecardByMatchIdRef.current;
              const mergedSc = mergeDecimalScorecardDelta(cardMap.get(matchIdStr) ?? null, snap.scorecard) as any;
              cardMap.set(matchIdStr, mergedSc);
              setScorecardData(mergedSc);
            }
            if (snap.scoregrid) {
              applyScoregridFromParsed(matchIdStr, snap);
            }
          })
          .catch(() => {});
      }
      return () => {
        const current = sharedSingleMatchSubscriptionRef.current;
        if (!current || current.matchId !== matchIdStr) return;
        current.subscribers = Math.max(0, current.subscribers - 1);
        if (current.subscribers === 0) {
          current.close();
        }
      };
    }
    if (existingShared && existingShared.matchId !== matchIdStr) {
      existingShared.close();
      sharedSingleMatchSubscriptionRef.current = null;
    }

    // Remount / lobby↔match-play: drop previous visit's last_update guards before cache replay,
    // otherwise a fresher event frame makes an older scorecard look "stale" and detail tabs stay empty.
    clearMatchFeedVersionGuards(matchIdStr);

    const url = `${baseApi}/${matchId}${streamUrlSuffix}`;
    setConnectionLost(false);
    /** Last time this subscription applied an SSE or snapshot payload — used to skip redundant snapshot polls. */
    const lastSseMessageAtMsRef = { current: 0 };
    const es = new EventSource(url, { withCredentials: false });

    es.addEventListener("open", () => {
      clearMatchFeedVersionGuards(matchIdStr);
      // Stream connected again (initial open or browser reconnect after deploy/network blip).
      setConnectionLost(false);
    });

    const processSingleMatchStreamPayload = (parsedData: any) => {
      if (!parsedData) {
        return;
      }
      if (parsedData.market_update) {
        parsedData = {
          ...parsedData,
          market_update: unwrapStreamMarketUpdate(parsedData.market_update),
        };
      }
      lastSseMessageAtMsRef.current = Date.now();
      if (parsedData.msg_type === "squares_overs_updated") {
        const mid = parsedData.match_id != null ? String(parsedData.match_id) : "";
        if (mid && mid === matchIdStr) {
          if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
            console.log("[Squares][stream] squares_overs_updated", {
              matchId: mid,
              payload: parsedData,
            });
          }
          setSquaresOversRefreshNonce((n) => n + 1);
        }
        return;
      }
      if (parsedData.msg_type === "bullion_settlement_updated") {
        const mid = parsedData.match_id != null ? String(parsedData.match_id) : "";
        if (mid && mid === matchIdStr) {
          const gameIds = Array.isArray(parsedData.game_ids)
            ? (parsedData.game_ids as unknown[])
                .map((g) => Number(g))
                .filter((n) => Number.isFinite(n) && n > 0)
            : [];
          if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
            console.log("[BULLION][stream] bullion_settlement_updated", {
              matchId: mid,
              gameIds,
            });
          }
          setBullionSettlementRefresh((prev) => ({
            nonce: prev.nonce + 1,
            matchId: mid,
            gameIds,
          }));
        }
        return;
      }
      if (parsedData.state) {
        setSubscriptionState(parsedData.state);
      }
      const luSingle = parsedData.last_update;
      applyScoregridFromParsed(matchIdStr, parsedData);

      const skipCricketSingle = isStaleCricketFeedVersion(matchIdStr, luSingle);
      const skipScorecardSingle = isStaleScorecardFeedVersion(matchIdStr, luSingle);
      const rawMarketsForBumpSingle = parsedData.market_update?.markets ?? [];
      const marketLuStaleSingle = isStaleMarketFeedVersion(matchIdStr, luSingle);
      const carrySettledMarketUpdatesSingle = rawMarketsForBumpSingle.some((m: unknown) =>
        liveMarketRowIndicatesFinalSettlement(m)
      );
      const skipMarketSingle =
        rawMarketsForBumpSingle.length > 0 &&
        marketLuStaleSingle &&
        !carrySettledMarketUpdatesSingle;

      if (parsedData.alerts) {
        if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "prod") {
          console.log("[Alerts] received (matchDetails):", JSON.stringify(parsedData.alerts, null, 2));
        }
        setAlerts(parsedData.alerts);
      }
      const battingAnalysisMsg = battingAnalysisFromMessage(parsedData);
      if (battingAnalysisMsg) setBattingAnalysis(battingAnalysisMsg);

      // ----------------------------- market_update -------------------------
      // Full books use market_snapshot (replace/evict). Empty markets do not overwrite.
      // `settled_market_keys` deletes Appendix-4 finals that SSE omits from `markets[]`.
      if (parsedData.market_update) {
        if (parsedData.market_update.event_status != null) {
          applyEventStatusFromFeed(parsedData.market_update.event_status);
        }
        if (parsedData.market_update.betting_status != null) {
          setMatchTradingStatus(parsedData.market_update.betting_status);
        }
        const rawMarkets = parsedData.market_update.markets ?? [];
        if (!skipMarketSingle && wantMarkets) {
          let bookChanged = false;
          if (rawMarkets.length > 0) {
            const fullSnapshot = streamMessageIsMarketSnapshot(parsedData);
            if (marketLuStaleSingle && carrySettledMarketUpdatesSingle) {
              applyMarketsFromStreamMessage(marketDataMapRef.current, rawMarkets, {
                staleSettledOnly: true,
              });
            } else {
              applyMarketsFromStreamMessage(marketDataMapRef.current, rawMarkets, {
                fullSnapshot,
              });
            }
            bookChanged = true;
          }
          if (applySettledMarketKeyRemovals(marketDataMapRef.current, parsedData)) {
            bookChanged = true;
          }
          if (bookChanged) scheduleMarketDataFlush();
          if (rawMarkets.length > 0) {
            applyWinProbabilityUpdate(parsedData, skipCricketSingle);
          }
        }
      }

      if (parsedData.event && !skipCricketSingle) {
        if (String(parsedData.event.event_description ?? "").includes("Pre Match Service")) {
          setPrematch(true);
          prematchRef.current = true;
          const preMatchScoreboard = eventScoreboardRows(parsedData.event);
          if (preMatchScoreboard.length > 0) {
            const scoreboard = preMatchScoreboard;
            const homeObj = scoreboard.find((item) => item.name === "home");
            const awayObj = scoreboard.find((item) => item.name === "away");
            applyTeamsFromFeed(
              homeObj ? homeObj.value : "",
              awayObj ? awayObj.value : ""
            );
          }
          setGamePhase((prev) => (prev ? prev : "Scheduled"));
        } else {
          if (prematchRef.current) {
            // Left Decimal pre-match feed while the page was already open — same hydrate as status go-live.
            requestLiveDetailHydrateRef.current?.();
          }
          setPrematch(false);
          prematchRef.current = false;
          const scoreboard = eventScoreboardRows(parsedData.event);
          if (scoreboard.length === 0) {
            // Slim lobby event frames may carry status without scoreboard rows.
          } else {
          const matchTitleObj = scoreboard.find(
            (item) => item.name === "matchtitle"
          );
          const game_phaseObj = scoreboard.find(
            (item) => item.name === "game_phase"
          );
          const local_timeObj = scoreboard.find(
            (item) => item.name === "local_time"
          );
          const homeObj = scoreboard.find((item) => item.name === "home");
          const awayObj = scoreboard.find((item) => item.name === "away");
          const battingObj = scoreboard.find(
            (item) => item.name === "Current Batting Team"
          );

          let localdt = new Date(local_timeObj ? local_timeObj.value : "");
          let formattedDate =
            localdt.getDate().toString().padStart(2, "0") +
            "/" +
            (localdt.getMonth() + 1).toString().padStart(2, "0") +
            "/" +
            localdt.getFullYear();

          let matchname = matchTitleObj ? matchTitleObj.value : "";
          const parts = matchname.split(",");
          if (parts.length > 2) {
            setMatchName(parts[0].trim());
            setLocation(parts[2].trim());
            setTournament(parts[1].trim());
          }
          setGamePhase((prev) => {
            const next = game_phaseObj?.value;
            if (next == null || String(next).trim() === "") return prev;
            return String(next);
          });
          setLocalTime(formattedDate);
          applyTeamsFromFeed(
            homeObj ? homeObj.value : "",
            awayObj ? awayObj.value : ""
          );

          setBatting(battingObj ? battingObj.value : "");

          const CurInns = scoreboard.find(
            (item) => item.name === "Current Innings"
          );
          const CurOvrs = scoreboard.find(
            (item) => item.name === "Current Innings Overs"
          );
          const CurPart = scoreboard.find(
            (item) => item.name === "Current Innings Part Overs"
          );
          setCurPos((prev) => {
            const next = [
              CurInns ? CurInns.value : (prev?.[0] ?? 0),
              CurOvrs ? CurOvrs.value : (prev?.[1] ?? 0),
              CurPart ? CurPart.value : (prev?.[2] ?? 0),
            ];
            if (
              prev &&
              prev.length >= 3 &&
              prev[0] === next[0] &&
              prev[1] === next[1] &&
              prev[2] === next[2]
            ) {
              return prev;
            }
            return next;
          });

          const maxOvers = scoreboard.find((item) => item.name === "Max Overs");
          if (maxOvers?.value != null && String(maxOvers.value).trim() !== "") {
            const liveMax = parseCricketFixtureFormatOvers(maxOvers.value);
            if (liveMax != null) setScoreboardMaxOvers(liveMax);
          }
          const reduced = scoreboardOversReduced(scoreboard);
          if (reduced != null) setOversReduced(reduced);
          const batting1 = scoreboard.find((item) => item.name === "batting.1");
          const batting1Runs = scoreboard.find(
            (item) => item.name === "runs.1"
          );
          const batting1Wkts = scoreboard.find(
            (item) => item.name === "wkts.1"
          );
          const batting1MaxOvers = scoreboard.find(
            (item) => item.name === "max.1"
          );
          const batting1Overs = scoreboard.find(
            (item) => item.name === "overs.1"
          );

          const batting2 = scoreboard.find((item) => item.name === "batting.2");
          const batting2Runs = scoreboard.find(
            (item) => item.name === "runs.2"
          );
          const batting2Wkts = scoreboard.find(
            (item) => item.name === "wkts.2"
          );
          const batting2MaxOvers = scoreboard.find(
            (item) => item.name === "max.2"
          );
          const batting2Overs = scoreboard.find(
            (item) => item.name === "overs.2"
          );

          let b1mover = batting1MaxOvers ? batting1MaxOvers.value : "";
          let b2mover = batting2MaxOvers ? batting2MaxOvers.value : "";
          let b1over = batting1Overs ? batting1Overs.value : "";
          let b2over = batting2Overs ? batting2Overs.value : "";

          let homeOver = b1over + "/" + b1mover;
          if (b1over === b1mover) {
            homeOver = b1mover;
          }
          let awayOver = b2over + "/" + b2mover;

          let bat1Runs = batting1Runs ? batting1Runs.value : "";
          let bat1Wkts = batting1Wkts ? batting1Wkts.value : "";
          let bat2Runs = batting2Runs ? batting2Runs.value : "";
          let bat2Wkts = batting2Wkts ? batting2Wkts.value : "";

          if (b2over === b2mover) {
            awayOver = b2mover;
          }
          if (awayOver === "") {
            awayOver = "0/" + (maxOvers?.value ?? "");
          }
          let bat1Prog = bat1Runs + "/" + bat1Wkts;
          if (bat1Prog === "/") {
            bat1Prog = "0/0";
          }
          let bat2Prog = bat2Runs + "/" + bat2Wkts;
          if (bat2Prog === "/") {
            bat2Prog = "0/0";
          }

          if ((batting1 ? batting1.value : "") === (homeObj?.value ?? "")) {
            guardedSetScore([bat1Prog, bat2Prog]);
            setOvers([homeOver, awayOver]);
          } else {
            guardedSetScore([bat2Prog, bat1Prog]);
            setOvers([awayOver, homeOver]);
          }

          const bat1 = scoreboard.find((item) =>
            item.name === battingObj ? battingObj.value : "" + "Bat.1"
          );
          const bat2 = scoreboard.find((item) =>
            item.name === battingObj ? battingObj.value : "" + "Bat.2"
          );
          const strike1 = scoreboard.find(
            (item) => item.name === "Batsman1 Strike"
          );
          const strike2 = scoreboard.find(
            (item) => item.name === "Batsman2 Strike"
          );
          let bowl1 = scoreboard.find((item) => item.name === "bowler1name");
          let bowl2 = scoreboard.find((item) => item.name === "bowler2name");
          const runs1 = scoreboard.find(
            (item) => item.name === "Batsman1 Runs"
          );
          const runs2 = scoreboard.find(
            (item) => item.name === "Batsman2 Runs"
          );
          let bowl1overs = scoreboard.find(
            (item) => item.name === "bowler1overs"
          );
          let bowl1runs = scoreboard.find(
            (item) => item.name === "bowler1runs"
          );
          let bowl1wicks = scoreboard.find(
            (item) => item.name === "bowler1wickets"
          );
          let bowl2overs = scoreboard.find(
            (item) => item.name === "bowler2overs"
          );
          let bowl2runs = scoreboard.find(
            (item) => item.name === "bowler2runs"
          );
          let bowl2wicks = scoreboard.find(
            (item) => item.name === "bowler2wickets"
          );
          const balls1 = scoreboard.find((item) =>
            item.name === battingObj ? battingObj.value : "" + "Bat.1balls"
          );
          const balls2 = scoreboard.find((item) =>
            item.name === battingObj ? battingObj.value : "" + "Bat.2balls"
          );
          let strikerbatter = "";
          let batterruns = 0;
          let batterballs = 0;
          if (strike1 && strike1.value === "1") {
            strikerbatter = bat1 ? bat1.value : 0;
            batterruns = runs1?.value ?? 0;
            batterballs = balls1?.value ?? 0;
          } else {
            strikerbatter = bat2 ? bat2.value : 0;
            batterruns = runs2?.value ?? 0;
            batterballs = balls2?.value ?? 0;
          }

          let actbowl = scoreboard.find((item) => item.name === "bowler");
          let bowlovers = "0 overs";
          let bowlruns = "0";
          let bowlwickets = "0";
          if (bowl1?.value !== undefined && bowl1.value !== "") {
            actbowl = bowl1.value;
            bowlovers = (bowl1overs?.value ?? "") + " overs";
            bowlruns = bowl1runs?.value ?? "";
            bowlwickets = bowl1wicks?.value ?? "";
          } else {
            actbowl = bowl2?.value ?? "";
            bowlovers = (bowl2overs?.value ?? "") + " overs";
            bowlruns = bowl2runs?.value ?? "";
            bowlwickets = bowl2wicks?.value ?? "";
          }
          setPartnership({
            bowler: actbowl,
            bowlerover: bowlovers,
            bowlerruns: bowlruns,
            bowlerwickets: bowlwickets,
            batter: strikerbatter,
            batterruns: batterruns,
            batterballs: batterballs,
          });
          }
        }
      }
      // ------------ score card ------------
      if (parsedData.event && !skipCricketSingle) {
        EventProc(parsedData.event, matchId);
      }

      // ---------------- match details -----------------
      matchDetails(parsedData, matchId, cb, {
        skipCricketSnapshot: skipCricketSingle,
        skipMarketData: skipMarketSingle,
        skipScorecard: skipScorecardSingle,
        wantMarkets,
      });

      //-------------------------- match-play -----------------------------
      if (parsedData.event && !skipCricketSingle) {
        const eventScoreboard = eventScoreboardRows(parsedData.event);
        const winner =
          eventScoreboard.find((item) => item.name === "winner")
            ?.value ?? "NA";
        const curOvs =
          eventScoreboard.find(
            (item) => item.name === "Current Innings Overs"
          )?.value ?? "";
        const curDelv =
          eventScoreboard.find(
            (item) => item.name === "current.delv"
          )?.value ?? "";
        const matchComment =
          eventScoreboard.find(
            (item) => item.name === "match_comment"
          )?.value ?? "";
        const gamePhase =
          eventScoreboard.find((item) => item.name === "game_phase")
            ?.value ?? "";
        const currentBowler =
          eventScoreboard.find((item) => item.name === "bowler")
            ?.value ?? "";
        const currentBatting =
          eventScoreboard.find(
            (item) => item.name === "Current Batting Team"
          )?.value ?? "";
        const batsman1 =
          eventScoreboard.find(
            (item) => item.name === currentBatting + "Bat.1"
          )?.value ?? "";
        const batsman2 =
          eventScoreboard.find(
            (item) => item.name === currentBatting + "Bat.2"
          )?.value ?? "";

        const strike1 = eventScoreboard.find(
          (item) => item.name === "Batsman1 Strike"
        );
        const strike2 = eventScoreboard.find(
          (item) => item.name === "Batsman2 Strike"
        );
        let ballStatus = "";
        if ((strike1?.value ?? "1") === "1") {
          ballStatus = currentBowler + " to " + batsman1;
        } else if ((strike2?.value ?? "1") === "1") {
          ballStatus = currentBowler + " to " + batsman2;
        }
        if (!isDecimalTeamWinner(winner)) {
          setStatus(ballStatus);
          setCurBall(curOvs + "." + curDelv);
          setCurInnsOver(curOvs);
        } else {
          setStatus(winner + " wins.");
          setCurBall(null);
        }
      }

      if (parsedData.commentary) {
        setCommentary((prevC) => mergeCommentariesIntoState(prevC, parsedData.commentary));
      }
      if (parsedData.scoreboard && !skipCricketSingle) {
        setBattings([
          parsedData.scoreboard.innings1battingteam,
          parsedData.scoreboard.innings2battingteam,
          parsedData.scoreboard.innings3battingteam,
          parsedData.scoreboard.innings4battingteam,
        ]);
        let stat = parsedData.scoreboard?.status.split("-")[0];
        if (
          parsedData.scoreboard.innings1battingteam ===
          parsedData.scoreboard.home
        ) {
          setHomeOvers([
            parsedData.scoreboard.innings1overs,
            parsedData.scoreboard.innings3overs,
          ]);
          setAwayOvers([
            parsedData.scoreboard.innings2overs,
            parsedData.scoreboard.innings4overs,
          ]);
        } else {
          setAwayOvers([
            parsedData.scoreboard.innings1overs,
            parsedData.scoreboard.innings3overs,
          ]);
          setHomeOvers([
            parsedData.scoreboard.innings2overs,
            parsedData.scoreboard.innings4overs,
          ]);
        }
        setMOvers([
          parsedData.scoreboard.innings1max,
          parsedData.scoreboard.innings2max,
          parsedData.scoreboard.innings3max,
          parsedData.scoreboard.innings4max,
        ]);
      }
      if (parsedData.scorecard && !skipScorecardSingle) {
        const cardMap = mergedScorecardByMatchIdRef.current;
        const mergedSc = mergeDecimalScorecardDelta(cardMap.get(matchIdStr) ?? null, parsedData.scorecard) as any;
        cardMap.set(matchIdStr, mergedSc);

        if (typeof mergedSc?.description === "string" && mergedSc.description.includes(",")) {
          const matchParts = mergedSc.description.split(",");
          if (matchParts[0]?.includes(" v ")) {
            setMatchTitle(matchParts.length >= 3 ? matchParts[1].trim() : "");
            applyTeamsFromMatchTitlePart(matchParts[0]);
          }
        }

        setInnsNow(mergedSc.inns_now);
        setBatNow(mergedSc.bat_now);
        const mp = typeof mergedSc?.description === "string" ? mergedSc.description.split(",") : [];
        if (mp[0]?.includes(" v ")) {
          const s1 = inningRunsWktsLine(mergedSc.inns1);
          const s2 = inningRunsWktsLine(mergedSc.inns2);
          const s3 = inningRunsWktsLine(mergedSc.inns3);
          const s4 = inningRunsWktsLine(mergedSc.inns4);
          const insScore = [s1, s2, s3, s4];
          const innsnow = mergedSc.inns_now;
          let oppnow = 1;
          let rptNow = 2;
          if (innsnow === 1 || innsnow === 3) {
            oppnow = 2;
            rptNow = 1;
          }
          setLiveBat(mergedSc.bat_now);
          if (mergedSc.bat_now === mp[0].split(" v ")[0]) {
            guardedSetScore([
              insScore[rptNow - 1],
              insScore[oppnow - 1],
              insScore[rptNow + 1],
              insScore[oppnow + 1],
            ]);
          } else {
            guardedSetScore([
              insScore[oppnow - 1],
              insScore[rptNow - 1],
              insScore[oppnow + 1],
              insScore[rptNow + 1],
            ]);
          }
        }
        setScorecardData(mergedSc);
        if (mergedSc.event_status != null) {
          applyEventStatusFromFeed(mergedSc.event_status);
        }
      }

      if (!skipCricketSingle && (parsedData.event || parsedData.scoreboard)) {
        bumpCricketFeedVersion(matchIdStr, luSingle);
      }
      if (parsedData.scorecard && !skipScorecardSingle) {
        bumpScorecardFeedVersion(matchIdStr, luSingle);
      }
      if (
        parsedData.market_update &&
        parseLastUpdateVersion(luSingle) != null &&
        !marketLuStaleSingle
      ) {
        bumpMarketFeedVersion(matchIdStr, luSingle);
      }
      //-------------------- match-play --------------------------

      //---------------------- trader-match-card ------------------------
      //----------------------- trader-match-card --------------------------
    };

    es.addEventListener("message", (event: MessageEvent) => {
      let parsedData;
      try {
        parsedData = JSON.parse(event.data as string);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'prod') {
          console.error('Could not parse event data', e.message);
        }
        return;
      }
      processSingleMatchStreamPayload(parsedData);
    });

    let matchSnapshotFetchInFlight = false;
    let pendingForceMatchSnapshot = false;
    const fetchMatchSnapshot = (opts?: { force?: boolean }) => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      // Trader needs margined odds; the primed cache is filled by non-Trader lobby flows (raw prices),
      // so Trader always fetches its own margined initial-state instead of reusing a primed snapshot.
      const primed = opts?.force ? null : productQuery ? null : consumePrimedMatchInitialState(matchIdStr);
      if (primed) {
        processSingleMatchStreamPayload(primed as Record<string, unknown>);
        return;
      }
      const streamBase = (baseApi || "").replace(/\/stream.*$/i, "");
      if (!streamBase) return;
      const now = Date.now();
      if (
        !opts?.force &&
        lastSseMessageAtMsRef.current > 0 &&
        now - lastSseMessageAtMsRef.current < TRADER_MATCH_SNAPSHOT_SKIP_IF_SSE_RECENT_MS
      ) {
        return;
      }
      if (matchSnapshotFetchInFlight) {
        if (opts?.force) pendingForceMatchSnapshot = true;
        return;
      }
      matchSnapshotFetchInFlight = true;
      fetch(
        `${streamBase}/stream/initial-state?matchIds=${encodeURIComponent(matchIdStr)}${initialStateExtraQuery}`,
        { credentials: "omit" }
      )
        .then((r) => (r.ok ? r.json() : null))
        .then((state: Record<string, unknown> | null) => {
          if (!state || typeof state !== "object") return;
          const snap = state[matchIdStr] as Record<string, unknown> | undefined;
          if (snap && typeof snap === "object") processSingleMatchStreamPayload(snap as any);
        })
        .catch(() => {})
        .finally(() => {
          matchSnapshotFetchInFlight = false;
          if (pendingForceMatchSnapshot) {
            pendingForceMatchSnapshot = false;
            fetchMatchSnapshot({ force: true });
          }
        });
    };

    requestLiveDetailHydrateRef.current = () => {
      if (liveDetailHydrateDoneRef.current.has(matchIdStr)) return;
      liveDetailHydrateDoneRef.current.add(matchIdStr);
      clearMatchFeedVersionGuards(matchIdStr);
      lastSseMessageAtMsRef.current = 0;
      fetchMatchSnapshot({ force: true });
    };

    fetchMatchSnapshot();
    const snapshotPollId = window.setInterval(fetchMatchSnapshot, TRADER_MATCH_SNAPSHOT_POLL_MS);

    es.addEventListener("error", function error() {
      setConnectionLost(true);
      lastSseMessageAtMsRef.current = 0;
      // Do not keep showing pre-outage odds/lines (e.g. old “Runs in Over 7”) after events server restart:
      // clear merged markets and reset monotonic last_update guards so the next snapshot/SSE payload is applied
      // even if Decimal’s last_update sequence restarted lower than our previous client ref.
      if (marketDataFlushTimeoutRef.current !== null) {
        clearTimeout(marketDataFlushTimeoutRef.current);
        marketDataFlushTimeoutRef.current = null;
      }
      marketDataMapRef.current.clear();
      setMarketData([]);
      clearMatchFeedVersionGuards(matchIdStr);
    });
    const closeSharedStream = () => {
      window.clearInterval(snapshotPollId);
      es.close();
      setConnectionLost(false);
      if (requestLiveDetailHydrateRef.current) {
        requestLiveDetailHydrateRef.current = null;
      }
      if (sharedSingleMatchSubscriptionRef.current?.matchId === matchIdStr) {
        sharedSingleMatchSubscriptionRef.current = null;
      }
    };

    sharedSingleMatchSubscriptionRef.current = {
      matchId: matchIdStr,
      subscribers: 1,
      close: closeSharedStream,
    };

    return () => {
      const current = sharedSingleMatchSubscriptionRef.current;
      if (!current || current.matchId !== matchIdStr) return;
      current.subscribers = Math.max(0, current.subscribers - 1);
      if (current.subscribers === 0) {
        current.close();
      }
    };
  }, [
    scheduleMarketDataFlush,
    applyWinProbabilityUpdate,
    EventProc,
    matchDetails,
    fetchScoreGrid,
    applyScoregridFromParsed,
    isStaleCricketFeedVersion,
    isStaleMarketFeedVersion,
    isStaleScorecardFeedVersion,
    bumpCricketFeedVersion,
    bumpMarketFeedVersion,
    bumpScorecardFeedVersion,
    clearMatchFeedVersionGuards,
  ]);

  useEffect(() => {
    return () => {
      sharedSingleMatchSubscriptionRef.current?.close();
      sharedSingleMatchSubscriptionRef.current = null;
    };
  }, []);

  /** Overs bowled in current innings = Current Innings Overs + Current Innings Part Overs / 6 (Decimal API). */
  const currentInningsOversDecimal = useMemo(() => {
    if (!CurPos || CurPos.length < 3) return null;
    const full = Number(CurPos[1]);
    const part = Number(CurPos[2]);
    if (Number.isNaN(full) || Number.isNaN(part)) return null;
    return full + part / 6;
  }, [CurPos]);

  const contextValue = useMemo(() => ({
        subscribeToMatches,
        subscribeToMatch,
        getCompletedData,
        fetchEvents,
        matchTitle,
        teams,
        marketData,
        tournament,
        score,
        overs,
        scoregrid,
        matchComment,
        runRate,
        currentInnings,
        batting,
        partnership,
        matchName,
        probability,
        lineup,
        format,
        scoreboardMaxOvers,
        oversReduced,
        competitionGender,
        scoreGridData,
        trading,
        fullComm,
        scorecardData,
        alerts,
        battingAnalysis,
        ballResults,
        location,
        localTime,
        game_phase,
        prematch,
        status,
        curBall,
        curInnsOver,
        overDelivery,
        commentary,
        battings,
        homeOvers,
        awayOvers,
        mOvers,
        innsNow,
        batNow,
        liveBat,
        CurPos,
        currentInningsOversDecimal,
        striker,
        battingOvers,
        bowling,
        incomingEvent,
        stream,
        feed,
        subHeader,
        eventStatus,
        matchTradingStatus,
        subscriptionState,
        connectionLost,
        matchStartDateTime,
        setSubHeader,
        subscribedMatchId,
        squaresOversRefreshNonce,
        bullionSettlementRefresh,
        squaresStaleScoregridBallSupplement,
      }), [
        subscribeToMatches,
        subscribeToMatch,
        getCompletedData,
        fetchEvents,
        matchTitle,
        teams,
        marketData,
        tournament,
        score,
        overs,
        scoregrid,
        matchComment,
        runRate,
        currentInnings,
        batting,
        partnership,
        matchName,
        probability,
        lineup,
        format,
        scoreboardMaxOvers,
        oversReduced,
        competitionGender,
        scoreGridData,
        trading,
        fullComm,
        scorecardData,
        alerts,
        battingAnalysis,
        ballResults,
        location,
        localTime,
        game_phase,
        prematch,
        status,
        curBall,
        curInnsOver,
        overDelivery,
        commentary,
        battings,
        homeOvers,
        awayOvers,
        mOvers,
        innsNow,
        batNow,
        liveBat,
        CurPos,
        striker,
        battingOvers,
        bowling,
        incomingEvent,
        stream,
        feed,
        subHeader,
        eventStatus,
        matchTradingStatus,
        subscriptionState,
        connectionLost,
        matchStartDateTime,
        subscribedMatchId,
        currentInningsOversDecimal,
        squaresOversRefreshNonce,
        bullionSettlementRefresh,
        squaresStaleScoregridBallSupplement,
      ]);

  useLayoutEffect(() => {
    socketStoreSnapshotRef.current = contextValue;
    notifySocketStoreListeners();
  }, [contextValue]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
